import React, { useState, useRef, useEffect } from 'react';
import { Storybook, Page, TextElement, ImageElement } from '../types/storybook';
import { getImageUrl, storybookAPI, aiAPI } from '../utils/api';
import GenerationProgressModal from './GenerationProgressModal';
import GalleryModal from './GalleryModal';
import './Sidebar.css';

interface SidebarProps {
  storybook: Storybook;
  onAddImage: (imageUrl: string, x?: number, y?: number, prompt?: string, characterName?: string) => void;
  onGenerateCharacter: (name: string, prompt: string, referenceImages?: string[], model?: 'chatgpt' | 'gemini', backgroundOption?: 'transparent' | 'full-color', chromaKeyColor?: 'white' | 'green' | 'blue', heightCategory?: 'tiny' | 'small' | 'medium' | 'tall' | 'very-tall') => Promise<string | undefined>;
  onGenerateBackground: (name: string, prompt: string, referenceImages?: string[], model?: 'chatgpt' | 'gemini', viewTransforms?: string[]) => Promise<string | undefined>;
  onGenerateScene: (name: string, scenePrompt: string, characterNames: string[], assetNames: string[], backgroundName?: string, cameraView?: string, backgroundOption?: 'transparent' | 'full-color', chromaKeyColor?: 'white' | 'green' | 'blue', model?: 'chatgpt' | 'gemini') => Promise<string | undefined>;
  onGenerateAsset: (name: string, prompt: string, referenceImages?: string[], model?: 'chatgpt' | 'gemini', backgroundOption?: 'transparent' | 'full-color', chromaKeyColor?: 'white' | 'green' | 'blue') => Promise<string | undefined>;
  onDeleteAsset?: (imageUrl: string) => void;
  onDeleteCharacter?: (imageUrl: string) => void;
  onDeleteBackground?: (imageUrl: string) => void;
  onDeleteScene?: (imageUrl: string) => void;
  onUploadAsset?: (file: File, name: string) => Promise<void>;
  onUploadCharacter?: (file: File, name: string) => Promise<void>;
  onUploadBackground?: (file: File, name: string) => Promise<void>;
  onUploadScene?: (file: File, name: string) => Promise<void>;
  onRefreshStorybook?: () => Promise<void>;
  hasApiAccess: boolean;
  // Element editing props
  selectedElement?: string | null;
  currentPage?: Page;
  onUpdateTextElement?: (id: string, updates: Partial<TextElement>) => void;
  onUpdateImageElement?: (id: string, updates: Partial<ImageElement>) => void;
  onDeleteElement?: () => void;
  onCropImage?: () => void;
  onRephraseText?: (textId: string, content: string) => Promise<void>;
  isRephrasing?: boolean;
  onSaveImageToScene?: (imageUrl: string) => Promise<void>;
  onUpscaleImage?: (imageUrl: string, upscaleFactor: 'x2' | 'x3' | 'x4') => Promise<string>;
  onEditImage?: (imageUrl: string, prompt: string, name: string, type: 'asset' | 'character' | 'background' | 'scene', backgroundOption?: 'transparent' | 'full-color', chromaKeyColor?: 'white' | 'green' | 'blue', fromCanvas?: boolean, canvasImageId?: string, maskImageUrl?: string, lassoData?: { points: {x: number, y: number}[], mode: string, canvasWidth: number, canvasHeight: number }, referenceImages?: string[], aspectRatio?: string, cameraAngle?: string) => Promise<string | undefined>;
  onBringToFront?: (id: string, type: 'text' | 'image') => void;
  onSendToBack?: (id: string, type: 'text' | 'image') => void;
  onShowImageHistory?: (element: ImageElement) => void;
  onUpdateGalleryImage?: (oldUrl: string, newUrl: string) => void;
  onUpdateCharacterHeight?: (characterIndex: number, heightCategory: 'tiny' | 'small' | 'medium' | 'tall' | 'very-tall') => void;
}

type InspectorTab = 'properties' | 'assets';

const readInspectorTab = (storybookId: string): InspectorTab => {
  if (typeof window === 'undefined') return 'assets';
  try {
    return window.localStorage.getItem(`tiny-little-fox:inspector-tab:${storybookId}`) === 'properties'
      ? 'properties'
      : 'assets';
  } catch {
    return 'assets';
  }
};

const Sidebar: React.FC<SidebarProps> = ({
  storybook,
  onAddImage,
  onGenerateCharacter,
  onGenerateBackground,
  onGenerateScene,
  onGenerateAsset,
  onDeleteAsset,
  onDeleteCharacter,
  onDeleteBackground,
  onDeleteScene,
  onUploadAsset,
  onUploadCharacter,
  onUploadBackground,
  onUploadScene,
  onRefreshStorybook,
  hasApiAccess,
  selectedElement,
  currentPage,
  onUpdateTextElement,
  onUpdateImageElement,
  onDeleteElement,
  onCropImage,
  onRephraseText,
  isRephrasing,
  onSaveImageToScene,
  onUpscaleImage,
  onEditImage,
  onBringToFront,
  onSendToBack,
  onShowImageHistory,
  onUpdateGalleryImage,
  onUpdateCharacterHeight
}) => {
  const [activeInspectorTab, setActiveInspectorTab] = useState<InspectorTab>(
    selectedElement ? 'properties' : readInspectorTab(storybook._id)
  );

  useEffect(() => {
    if (selectedElement) setActiveInspectorTab('properties');
  }, [selectedElement]);

  useEffect(() => {
    try {
      window.localStorage.setItem(`tiny-little-fox:inspector-tab:${storybook._id}`, activeInspectorTab);
    } catch {
      // Ignore storage restrictions; tab switching remains available.
    }
  }, [activeInspectorTab, storybook._id]);

  const handleInspectorTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextTab = event.key === 'ArrowLeft' || event.key === 'Home'
      ? 'properties'
      : 'assets';
    setActiveInspectorTab(nextTab);
    window.requestAnimationFrame(() => {
      document.getElementById(`editor-${nextTab}-tab`)?.focus();
    });
  };

  // Helper function to convert rgba/rgb colors to hex format for color inputs
  const toHexColor = (color: string | undefined, defaultColor: string = '#000000'): string => {
    if (!color) return defaultColor;
    // If already hex, return as is
    if (color.startsWith('#')) return color;
    // Convert rgba/rgb to hex
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const r = parseInt(match[1]).toString(16).padStart(2, '0');
      const g = parseInt(match[2]).toString(16).padStart(2, '0');
      const b = parseInt(match[3]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
    return defaultColor;
  };

  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const [showSceneModal, setShowSceneModal] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [assetPrompt, setAssetPrompt] = useState('');
  const [characterName, setCharacterName] = useState('');
  const [characterPrompt, setCharacterPrompt] = useState('');
  const [backgroundName, setBackgroundName] = useState('');
  const [backgroundPrompt, setBackgroundPrompt] = useState('');
  const [sceneName, setSceneName] = useState('');
  const [scenePrompt, setScenePrompt] = useState('');
  const [selectedCharacters, setSelectedCharacters] = useState<string[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedBackground, setSelectedBackground] = useState<string>('');
  const [cameraView, setCameraView] = useState<string>('');
  const [sceneBackgroundOption, setSceneBackgroundOption] = useState<'transparent' | 'full-color'>('full-color');
  const [chromaKeyColor, setChromaKeyColor] = useState<'white' | 'green' | 'blue'>('green');
  const [characterBackgroundOption, setCharacterBackgroundOption] = useState<'transparent' | 'full-color'>('full-color');
  const [characterChromaKeyColor, setCharacterChromaKeyColor] = useState<'white' | 'green' | 'blue'>('white');
  const [assetBackgroundOption, setAssetBackgroundOption] = useState<'transparent' | 'full-color'>('transparent');
  const [assetChromaKeyColor, setAssetChromaKeyColor] = useState<'white' | 'green' | 'blue'>('green');
  const [characterModel, setCharacterModel] = useState<'chatgpt' | 'gemini'>('gemini');
  const [characterHeightCategory, setCharacterHeightCategory] = useState<'tiny' | 'small' | 'medium' | 'tall' | 'very-tall'>('medium');
  const [assetModel, setAssetModel] = useState<'chatgpt' | 'gemini'>('gemini');
  const [backgroundModel, setBackgroundModel] = useState<'chatgpt' | 'gemini'>('gemini');
  const sceneModel = 'gemini'; // Scene composition only supports Gemini
  // Reference selections for generation
  const [assetRefImages, setAssetRefImages] = useState<string[]>([]);
  const [characterRefImages, setCharacterRefImages] = useState<string[]>([]);
  const [backgroundRefImages, setBackgroundRefImages] = useState<string[]>([]);
  const [backgroundViewTransforms, setBackgroundViewTransforms] = useState<string[]>([]);
  // Individual view transform selectors
  const [bgRotation, setBgRotation] = useState<string>('');
  const [bgVerticalAngle, setBgVerticalAngle] = useState<string>('');
  const [bgZoom, setBgZoom] = useState<string>('');
  const [bgPan, setBgPan] = useState<string>('');
  const [bgSpecialView, setBgSpecialView] = useState<string>('');
  const [isDeleteZoneActive, setIsDeleteZoneActive] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteItemType, setDeleteItemType] = useState<'asset' | 'character' | 'background' | 'scene' | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteItemName, setDeleteItemName] = useState<string | null>(null);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [showUpscaleDropdown, setShowUpscaleDropdown] = useState(false);
  const [isCanvasUpscaling, setIsCanvasUpscaling] = useState(false);
  const [isApplyingTransparency, setIsApplyingTransparency] = useState(false);
  const [showTransparencyLoadingModal, setShowTransparencyLoadingModal] = useState(false);
  const [transparencyLoadingMessage, setTransparencyLoadingMessage] = useState('');
  
  // Edit image modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editImageThumbnailUrl, setEditImageThumbnailUrl] = useState(''); // Add thumbnail URL
  const [editImageName, setEditImageName] = useState('');
  const [editNewName, setEditNewName] = useState('');
  const [editImageType, setEditImageType] = useState<'asset' | 'character' | 'background' | 'scene'>('asset');
  const [editPrompt, setEditPrompt] = useState('');
  const [editOriginalPrompt, setEditOriginalPrompt] = useState('');
  const [editRefImages, setEditRefImages] = useState<string[]>([]);
  const [editBackgroundOption, setEditBackgroundOption] = useState<'transparent' | 'full-color'>('full-color');
  const [editChromaKeyColor, setEditChromaKeyColor] = useState<'white' | 'green' | 'blue'>('white');
  const [editFromCanvas, setEditFromCanvas] = useState(false);
  const [editCanvasImageId, setEditCanvasImageId] = useState<string | null>(null);
  const [editAspectRatio, setEditAspectRatio] = useState<string>(''); // Will be set to canvas aspect ratio by default
  const [editCameraAngle, setEditCameraAngle] = useState<string>('eye-level');
  const [isEditing, setIsEditing] = useState(false);
  
  // Lasso selection states
  const [lassoPoints, setLassoPoints] = useState<{x: number, y: number}[]>([]);
  const [isDrawingLasso, setIsDrawingLasso] = useState(false);
  const [lassoMode, setLassoMode] = useState<'none' | 'inside' | 'outside'>('none');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const [isCanvasReady, setIsCanvasReady] = useState(false); // Track if canvas is properly positioned
  const lassoPointsRef = useRef<{x: number, y: number}[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const drawRequestRef = useRef<number | null>(null);

  // Generation progress modal states
  const [showGenerationProgress, setShowGenerationProgress] = useState(false);
  const [generationType, setGenerationType] = useState<'character' | 'background' | 'scene' | 'asset'>('character');
  const [generationItemName, setGenerationItemName] = useState<string>('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [currentGenerationModel, setCurrentGenerationModel] = useState<'chatgpt' | 'gemini'>('gemini');

  // Thumbnail generation queue to prevent rate limiting
  const thumbnailQueueRef = useRef<Array<{
    imageUrl: string;
    storybookId: string;
    imageType: 'character' | 'asset' | 'background' | 'scene';
    imageName: string;
  }>>([]);
  const isProcessingQueueRef = useRef(false);
  const processedThumbnailsRef = useRef<Set<string>>(new Set());
  const failedThumbnailsRef = useRef<Set<string>>(new Set()); // Track failed generations
  const requestedThumbnailsRef = useRef<Set<string>>(new Set()); // Track requested (in queue or processing)
  const needsRefreshRef = useRef(false); // Track if we need to refresh after queue processing

  // Aspect ratio lock for image resizing
  const [aspectRatioLocked, setAspectRatioLocked] = useState(true);
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);

  // Position canvas when edit modal opens and image loads
  useEffect(() => {
    if (showEditModal && imageElement && canvasRef.current) {
      const positionCanvas = () => {
        if (imageElement && canvasRef.current) {
          const imgRect = imageElement.getBoundingClientRect();
          const wrapperRect = imageElement.parentElement?.getBoundingClientRect();
          if (wrapperRect && imgRect.width > 0 && imgRect.height > 0) {
            const canvas = canvasRef.current;
            canvas.style.left = `${imgRect.left - wrapperRect.left}px`;
            canvas.style.top = `${imgRect.top - wrapperRect.top}px`;
            canvas.style.width = `${imgRect.width}px`;
            canvas.style.height = `${imgRect.height}px`;
            console.log('Canvas positioned:', { left: canvas.style.left, top: canvas.style.top, width: canvas.style.width, height: canvas.style.height });
            setIsCanvasReady(true); // Mark canvas as ready
          }
        }
      };

      // Reset canvas ready state
      setIsCanvasReady(false);
      
      // Position immediately
      positionCanvas();
      
      // Position after a short delay to ensure image is rendered
      const timer = setTimeout(positionCanvas, 100);
      
      return () => clearTimeout(timer);
    } else if (!showEditModal) {
      // Reset when modal closes
      setIsCanvasReady(false);
    }
  }, [showEditModal, imageElement]);

  // Store last generation parameters for regeneration
  const [lastGenerationParams, setLastGenerationParams] = useState<{
    type: 'character' | 'background' | 'scene' | 'asset';
    name: string;
    prompt: string;
    refImages?: string[];
    characters?: string[];
    assets?: string[];
    background?: string;
    cameraView?: string;
  } | null>(null);

  // Gallery modal state
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  const handleRegenerate = () => {
    if (!lastGenerationParams) return;
    
    const { type, name, prompt, refImages, characters, assets, background, cameraView } = lastGenerationParams;
    
    // Increment name with a counter for comparison
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(/:/g, '');
    const newName = `${name}_v${timestamp}`;
    
    switch (type) {
      case 'asset':
        setAssetName(newName);
        setAssetPrompt(prompt);
        setAssetRefImages(refImages || []);
        setShowAssetModal(true);
        break;
      case 'character':
        setCharacterName(newName);
        setCharacterPrompt(prompt);
        setCharacterRefImages(refImages || []);
        setShowCharacterModal(true);
        break;
      case 'background':
        setBackgroundName(newName);
        setBackgroundPrompt(prompt);
        setBackgroundRefImages(refImages || []);
        setShowBackgroundModal(true);
        break;
      case 'scene':
        setSceneName(newName);
        setScenePrompt(prompt);
        setSelectedCharacters(characters || []);
        setSelectedAssets(assets || []);
        setSelectedBackground(background || '');
        setCameraView(cameraView || '');
        setShowSceneModal(true);
        break;
    }
  };

  /**
   * Helper function to get the appropriate image URL (thumbnail for gallery, full for canvas)
   * Also triggers lazy thumbnail generation if thumbnail doesn't exist
   * @param fullUrl - The full-size image URL
   * @param thumbnailUrl - The thumbnail URL (optional)
   * @param forGallery - Whether the image is for gallery display (true) or canvas (false)
   * @param imageType - Type of image (character, asset, background, scene)
   * @param imageName - Name of the image
   */
  const getThumbnailOrFullUrl = (
    fullUrl: string | undefined, 
    thumbnailUrl: string | undefined, 
    forGallery: boolean = true,
    imageType?: 'character' | 'asset' | 'background' | 'scene',
    imageName?: string
  ): string | undefined => {
    if (!fullUrl) return undefined;
    
    // Use thumbnail for gallery display if available
    if (forGallery && thumbnailUrl) {
      return thumbnailUrl;
    }
    
    // Check if thumbnail generation failed - if so, use full image
    const key = storybook?._id && imageType && imageName 
      ? `${storybook._id}-${imageType}-${imageName}` 
      : null;
    
    if (key && failedThumbnailsRef.current.has(key)) {
      return fullUrl;
    }
    
    // If thumbnail doesn't exist but should (for gallery), trigger lazy generation
    if (forGallery && !thumbnailUrl && imageType && imageName && storybook?._id && key) {
      // Only log and queue if not already requested or processed
      if (!requestedThumbnailsRef.current.has(key) && !processedThumbnailsRef.current.has(key)) {
        console.log('📸 Thumbnail missing, requesting generation for:', imageName);
        requestedThumbnailsRef.current.add(key);
        // Fire and forget - don't wait for response
        requestThumbnailGeneration(fullUrl, storybook._id, imageType, imageName);
      }
      
      // Return a data URL placeholder to avoid loading full image
      return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="14"%3EGenerating...%3C/text%3E%3C/svg%3E';
    }
    
    // For canvas or when thumbnail exists, use full URL
    return fullUrl;
  };

  /**
   * Process the thumbnail generation queue one item at a time
   */
  const processThumbnailQueue = async () => {
    if (isProcessingQueueRef.current || thumbnailQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    needsRefreshRef.current = false;

    while (thumbnailQueueRef.current.length > 0) {
      const item = thumbnailQueueRef.current.shift()!;
      
      // Skip if already processed
      const key = `${item.storybookId}-${item.imageType}-${item.imageName}`;
      if (processedThumbnailsRef.current.has(key)) {
        continue;
      }

      try {
        console.log(`🔄 Generating thumbnail for: ${item.imageName} (${thumbnailQueueRef.current.length} remaining in queue)`);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/storybooks/generate-thumbnail`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            imageUrl: item.imageUrl,
            storybookId: item.storybookId,
            imageType: item.imageType,
            imageName: item.imageName
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Thumbnail generated:', data.thumbnailUrl);
          processedThumbnailsRef.current.add(key);
          needsRefreshRef.current = true; // Mark that we need to refresh at the end
        } else if (response.status === 429) {
          console.warn('⏸️ Rate limited, waiting 2 seconds before retry...');
          // Put the item back in the queue
          thumbnailQueueRef.current.unshift(item);
          // Wait 2 seconds before retrying
          await new Promise(resolve => setTimeout(resolve, 2000));
        } else {
          // Other error (400, 500, etc.) - mark as failed and show full image
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error(`❌ Thumbnail generation failed for ${item.imageName}:`, errorData.error || response.statusText);
          failedThumbnailsRef.current.add(key);
          needsRefreshRef.current = true; // Mark that we need to refresh to show full image
        }
      } catch (error) {
        console.error(`❌ Network error generating thumbnail for ${item.imageName}:`, error);
        // Mark as failed so it shows full image instead of placeholder
        failedThumbnailsRef.current.add(key);
        needsRefreshRef.current = true; // Mark that we need to refresh to show full image
      }

      // Small delay between requests to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    isProcessingQueueRef.current = false;
    
    // Don't refresh storybook after thumbnail generation to avoid losing unsaved changes
    // Thumbnails will be loaded on next natural reload
    if (needsRefreshRef.current) {
      console.log('✅ Thumbnail processing complete (storybook will refresh on next load)');
      needsRefreshRef.current = false;
    }
  };

  /**
   * Request thumbnail generation for an image that doesn't have one
   * Adds to queue instead of making immediate request
   */
  const requestThumbnailGeneration = async (
    imageUrl: string,
    storybookId: string,
    imageType: 'character' | 'asset' | 'background' | 'scene',
    imageName: string
  ) => {
    const key = `${storybookId}-${imageType}-${imageName}`;
    
    // Skip if already processed or in queue
    if (processedThumbnailsRef.current.has(key)) {
      return;
    }

    // Check if already in queue
    const alreadyQueued = thumbnailQueueRef.current.some(
      item => item.storybookId === storybookId && 
              item.imageType === imageType && 
              item.imageName === imageName
    );

    if (!alreadyQueued) {
      console.log(`📥 Adding to thumbnail queue: ${imageName}`);
      thumbnailQueueRef.current.push({
        imageUrl,
        storybookId,
        imageType,
        imageName
      });

      // Start processing the queue
      processThumbnailQueue();
    }
  };

  const handleGenerateAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Store generation parameters for regeneration
    setLastGenerationParams({
      type: 'asset',
      name: assetName,
      prompt: assetPrompt,
      refImages: assetRefImages
    });
    
    setGenerationType('asset');
    setGenerationItemName(assetName);
    setGeneratedImageUrl(null);
    setCurrentGenerationModel(assetModel);
    setShowGenerationProgress(true);
    
    try {
      const imageUrl = await onGenerateAsset(assetName, assetPrompt, assetRefImages, assetModel, assetBackgroundOption, assetChromaKeyColor);
      setGeneratedImageUrl(imageUrl || null);
      setShowAssetModal(false);
      setAssetName('');
      setAssetPrompt('');
      setAssetRefImages([]);
      setAssetBackgroundOption('transparent');
      setAssetChromaKeyColor('green');
    } catch (error) {
      console.error('Failed to generate asset:', error);
    } finally {
      // Keep modal open to show the result
    }
  };

  const handleGenerateCharacter = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Store generation parameters for regeneration
    setLastGenerationParams({
      type: 'character',
      name: characterName,
      prompt: characterPrompt,
      refImages: characterRefImages
    });
    
    setGenerationType('character');
    setGenerationItemName(characterName);
    setGeneratedImageUrl(null);
    setCurrentGenerationModel(characterModel);
    setShowGenerationProgress(true);
    
    try {
      const imageUrl = await onGenerateCharacter(characterName, characterPrompt, characterRefImages, characterModel, characterBackgroundOption, characterChromaKeyColor, characterHeightCategory);
      setGeneratedImageUrl(imageUrl || null);
      setShowCharacterModal(false);
      setCharacterName('');
      setCharacterPrompt('');
      setCharacterRefImages([]);
      setCharacterBackgroundOption('full-color');
      setCharacterChromaKeyColor('white');
      setCharacterHeightCategory('medium');
    } catch (error) {
      console.error('Failed to generate character:', error);
    } finally {
      // Keep modal open to show the result
    }
  };

  const handleGenerateBackground = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Combine all selected view transforms
    const combinedTransforms: string[] = [];
    if (bgRotation) combinedTransforms.push(bgRotation);
    if (bgVerticalAngle) combinedTransforms.push(bgVerticalAngle);
    if (bgZoom) combinedTransforms.push(bgZoom);
    if (bgPan) combinedTransforms.push(bgPan);
    if (bgSpecialView) combinedTransforms.push(bgSpecialView);
    
    // Store generation parameters for regeneration
    setLastGenerationParams({
      type: 'background',
      name: backgroundName,
      prompt: backgroundPrompt,
      refImages: backgroundRefImages
    });
    
    setGenerationType('background');
    setGenerationItemName(backgroundName);
    setGeneratedImageUrl(null);
    setCurrentGenerationModel(backgroundModel);
    setShowGenerationProgress(true);
    
    try {
      const imageUrl = await onGenerateBackground(backgroundName, backgroundPrompt, backgroundRefImages, backgroundModel, combinedTransforms.length > 0 ? combinedTransforms : undefined);
      setGeneratedImageUrl(imageUrl || null);
      setShowBackgroundModal(false);
      setBackgroundName('');
      setBackgroundPrompt('');
      setBackgroundRefImages([]);
      setBgRotation('');
      setBgVerticalAngle('');
      setBgZoom('');
      setBgPan('');
      setBgSpecialView('');
    } catch (error) {
      console.error('Failed to generate background:', error);
    } finally {
      // Keep modal open to show the result
    }
  };

  const handleGenerateScene = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Store generation parameters for regeneration
    setLastGenerationParams({
      type: 'scene',
      name: sceneName,
      prompt: scenePrompt,
      characters: selectedCharacters,
      assets: selectedAssets,
      background: selectedBackground,
      cameraView: cameraView
    });
    
    setGenerationType('scene');
    setGenerationItemName(sceneName);
    setGeneratedImageUrl(null);
    setCurrentGenerationModel(sceneModel);
    setShowGenerationProgress(true);
    
    try {
      const imageUrl = await onGenerateScene(sceneName, scenePrompt, selectedCharacters, selectedAssets, selectedBackground, cameraView, sceneBackgroundOption, chromaKeyColor, sceneModel);
      setGeneratedImageUrl(imageUrl || null);
      setShowSceneModal(false);
      setSceneName('');
      setScenePrompt('');
      setSelectedCharacters([]);
      setSelectedAssets([]);
      setSelectedBackground('');
      setCameraView('');
      setSceneBackgroundOption('full-color');
    } catch (error) {
      console.error('Failed to generate scene:', error);
    } finally {
      // Keep modal open to show the result
    }
  };

  const toggleCharacterSelection = (characterName: string) => {
    setSelectedCharacters(prev => 
      prev.includes(characterName) 
        ? prev.filter(n => n !== characterName)
        : [...prev, characterName]
    );
  };

  const toggleAssetSelection = (assetName: string) => {
    setSelectedAssets(prev => 
      prev.includes(assetName) 
        ? prev.filter(n => n !== assetName)
        : [...prev, assetName]
    );
  };

  const toggleAssetRefSelection = (imageUrl: string) => {
    setAssetRefImages(prev => 
      prev.includes(imageUrl) 
        ? prev.filter(url => url !== imageUrl)
        : [...prev, imageUrl]
    );
  };

  const toggleCharacterRefSelection = (imageUrl: string) => {
    setCharacterRefImages(prev => 
      prev.includes(imageUrl) 
        ? prev.filter(url => url !== imageUrl)
        : [...prev, imageUrl]
    );
  };

  const toggleBackgroundRefSelection = (imageUrl: string) => {
    setBackgroundRefImages(prev => 
      prev.includes(imageUrl) 
        ? prev.filter(url => url !== imageUrl)
        : [...prev, imageUrl]
    );
  };

  const handleDragStart = (e: React.DragEvent, imageUrl: string, name?: string, prompt?: string) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('imageUrl', imageUrl);
    e.dataTransfer.setData('name', name || '');
    e.dataTransfer.setData('prompt', prompt || '');
  };

  const handleDeleteDragStart = (e: React.DragEvent, type: 'asset' | 'character' | 'background' | 'scene', name: string, imageUrl?: string) => {
    setIsDeleteZoneActive(true);
    setDeleteItemType(type);
    setDeleteItemName(name);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('deleteType', type);
    e.dataTransfer.setData('deleteName', name);
    e.dataTransfer.setData('deleteImageUrl', imageUrl || '');
  };

  // Gallery modal handlers
  const getGalleryImages = () => {
    const images: Array<{ id: string; url: string; name: string; type: 'asset' | 'character' | 'background' | 'scene' }> = [];
    
    // Add assets
    (storybook.assets || []).forEach((asset, index) => {
      if (asset.imageUrl) {
        images.push({
          id: `asset-${index}-${asset.name}`,
          url: getImageUrl(getThumbnailOrFullUrl(asset.imageUrl, asset.thumbnailUrl, true, 'asset', asset.name)!),
          name: asset.name,
          type: 'asset'
        });
      }
    });
    
    // Add characters
    (storybook.characters || []).forEach((character, index) => {
      if (character.imageUrl) {
        images.push({
          id: `character-${index}-${character.name}`,
          url: getImageUrl(getThumbnailOrFullUrl(character.imageUrl, character.thumbnailUrl, true, 'character', character.name)!),
          name: character.name,
          type: 'character'
        });
      }
    });
    
    // Add backgrounds
    (storybook.backgrounds || []).forEach((background, index) => {
      if (background.imageUrl) {
        images.push({
          id: `background-${index}-${background.name}`,
          url: getImageUrl(getThumbnailOrFullUrl(background.imageUrl, background.thumbnailUrl, true, 'background', background.name)!),
          name: background.name,
          type: 'background'
        });
      }
    });
    
    // Add scenes
    (storybook.scenes || []).forEach((scene, index) => {
      if (scene.imageUrl) {
        images.push({
          id: `scene-${index}-${scene.name}`,
          url: getImageUrl(getThumbnailOrFullUrl(scene.imageUrl, scene.thumbnailUrl, true, 'scene', scene.name)!),
          name: scene.name,
          type: 'scene'
        });
      }
    });
    
    return images;
  };

  const handleGalleryRename = (id: string, newName: string) => {
    const [type, indexStr] = id.split('-');
    const index = parseInt(indexStr);
    
    // Update storybook based on type
    const updatedStorybook = { ...storybook };
    
    switch (type) {
      case 'asset':
        if (updatedStorybook.assets && updatedStorybook.assets[index]) {
          updatedStorybook.assets[index].name = newName;
        }
        break;
      case 'character':
        if (updatedStorybook.characters && updatedStorybook.characters[index]) {
          updatedStorybook.characters[index].name = newName;
        }
        break;
      case 'background':
        if (updatedStorybook.backgrounds && updatedStorybook.backgrounds[index]) {
          updatedStorybook.backgrounds[index].name = newName;
        }
        break;
      case 'scene':
        if (updatedStorybook.scenes && updatedStorybook.scenes[index]) {
          updatedStorybook.scenes[index].name = newName;
        }
        break;
    }
    
    // Note: In a real app, you would call an API to update the storybook
    // For now, this updates the local state only
  };

  const handleGalleryDelete = (id: string) => {
    console.log('handleGalleryDelete called with id:', id);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [type, indexStr, ...nameParts] = id.split('-');
    const name = nameParts.join('-');
    console.log('Deleting:', { type, name });
    
    switch (type) {
      case 'asset':
        const asset = storybook.assets?.find(a => a.name === name);
        if (asset && asset.imageUrl && onDeleteAsset) {
          onDeleteAsset(asset.imageUrl);
        }
        break;
      case 'character':
        const character = storybook.characters?.find(c => c.name === name);
        if (character && character.imageUrl && onDeleteCharacter) {
          onDeleteCharacter(character.imageUrl);
        }
        break;
      case 'background':
        const background = storybook.backgrounds?.find(b => b.name === name);
        if (background && background.imageUrl && onDeleteBackground) {
          onDeleteBackground(background.imageUrl);
        }
        break;
      case 'scene':
        const scene = storybook.scenes?.find(s => s.name === name);
        if (scene && scene.imageUrl && onDeleteScene) {
          onDeleteScene(scene.imageUrl);
        }
        break;
    }
  };

  const handleGalleryMoveToType = async (id: string, newType: 'asset' | 'character' | 'background' | 'scene') => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [currentType, indexStr, ...nameParts] = id.split('-');
    const name = nameParts.join('-');
    
    if (currentType === newType) return;
    
    // Convert singular type to plural for API
    const fromTypeMap: Record<string, string> = {
      'asset': 'assets',
      'character': 'characters',
      'background': 'backgrounds',
      'scene': 'scenes'
    };
    
    const toTypeMap: Record<string, string> = {
      'asset': 'assets',
      'character': 'characters',
      'background': 'backgrounds',
      'scene': 'scenes'
    };
    
    const fromType = fromTypeMap[currentType];
    const toType = toTypeMap[newType];
    
    if (!fromType || !toType) return;
    
    try {
      // Get storybook ID
      const storybookId = (storybook as any)._id;
      if (!storybookId) {
        throw new Error('Storybook ID not found');
      }
      
      console.log(`Moving ${name} from ${fromType} to ${toType} in storybook ${storybookId}`);
      
      // Call backend API to move the image
      const response = await storybookAPI.moveImage(storybookId, fromType, toType, name);
      
      if (response.success) {
        console.log('Image moved successfully, refreshing storybook...');
        // Refresh the storybook to get updated data
        if (onRefreshStorybook) {
          await onRefreshStorybook();
          console.log('Storybook refreshed');
        }
      } else {
        throw new Error('Move operation failed');
      }
    } catch (error) {
      console.error(`Failed to move ${name} from ${currentType} to ${newType}:`, error);
      alert(`Failed to move image. Please try again.`);
    }
  };

  const handleDeleteDragEnd = () => {
    setIsDeleteZoneActive(false);
    setDeleteItemType(null);
    setDeleteItemName(null);
  };

  const handleDeleteDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('deleteType') as 'asset' | 'character' | 'background' | 'scene';
    const name = e.dataTransfer.getData('deleteName');
    const imageUrl = e.dataTransfer.getData('deleteImageUrl');
    
    if (type && name) {
      const confirmDelete = window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`);
      if (confirmDelete) {
        switch (type) {
          case 'asset':
            onDeleteAsset?.(imageUrl || name);
            break;
          case 'character':
            onDeleteCharacter?.(imageUrl || name);
            break;
          case 'background':
            onDeleteBackground?.(imageUrl || name);
            break;
          case 'scene':
            onDeleteScene?.(imageUrl || name);
            break;
        }
      }
    }
    setIsDeleteZoneActive(false);
    setDeleteItemType(null);
    setDeleteItemName(null);
  };

  const handleDeleteDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleUploadAsset = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadAsset) return;
    
    const name = window.prompt('Enter a name for this asset:');
    if (!name) return;
    
    try {
      await onUploadAsset(file, name);
      e.target.value = ''; // Reset input
    } catch (error) {
      console.error('Failed to upload asset:', error);
      alert('Failed to upload asset');
    }
  };

  const handleUploadCharacter = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadCharacter) return;
    
    const name = window.prompt('Enter a name for this character:');
    if (!name) return;
    
    try {
      await onUploadCharacter(file, name);
      e.target.value = ''; // Reset input
    } catch (error) {
      console.error('Failed to upload character:', error);
      alert('Failed to upload character');
    }
  };

  const handleUploadBackground = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadBackground) return;
    
    const name = window.prompt('Enter a name for this background:');
    if (!name) return;
    
    try {
      await onUploadBackground(file, name);
      e.target.value = ''; // Reset input
    } catch (error) {
      console.error('Failed to upload background:', error);
      alert('Failed to upload background');
    }
  };

  const handleUploadScene = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadScene) return;
    
    const name = window.prompt('Enter a name for this scene:');
    if (!name) return;
    
    try {
      await onUploadScene(file, name);
      e.target.value = ''; // Reset input
    } catch (error) {
      console.error('Failed to upload scene:', error);
      alert('Failed to upload scene');
    }
  };

  const handleOpenEditModal = (imageUrl: string, name: string, type: 'asset' | 'character' | 'background' | 'scene', originalPrompt?: string, fromCanvas: boolean = false, canvasImageId?: string) => {
    setEditImageUrl(imageUrl);
    setEditImageName(name);
    
    // Generate unique name by checking for duplicates
    let baseName = `${name} - edited`;
    let newName = baseName;
    let counter = 1;
    
    // Check for duplicates in the appropriate gallery
    const existingNames: string[] = [];
    if (storybook) {
      switch (type) {
        case 'asset':
          existingNames.push(...(storybook.assets || []).map(a => a.name));
          break;
        case 'character':
          existingNames.push(...(storybook.characters || []).map(c => c.name));
          break;
        case 'background':
          existingNames.push(...(storybook.backgrounds || []).map(b => b.name));
          break;
        case 'scene':
          existingNames.push(...(storybook.scenes || []).map(s => s.name));
          break;
      }
    }
    
    // Find unique name
    while (existingNames.includes(newName)) {
      counter++;
      newName = `${baseName} ${counter}`;
    }
    
    setEditNewName(newName);
    setEditImageType(type);
    setEditPrompt('');
    setEditOriginalPrompt(originalPrompt || '');
    setEditRefImages([]);
    setEditFromCanvas(fromCanvas);
    setEditCanvasImageId(canvasImageId || null);
    // Set default background options based on type - default to full-color for all
    if (type === 'character') {
      setEditBackgroundOption('full-color');
      setEditChromaKeyColor('white');
    } else if (type === 'asset') {
      setEditBackgroundOption('full-color');
      setEditChromaKeyColor('green');
    } else if (type === 'scene') {
      setEditBackgroundOption('full-color');
      setEditChromaKeyColor('green');
    } else {
      setEditBackgroundOption('full-color');
      setEditChromaKeyColor('white');
    }
    
    // Calculate aspect ratio from the actual image being edited
    // Gemini supports: 1:1, 3:4, 4:3, 9:16, 16:9
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const imageAspectRatio = img.naturalWidth / img.naturalHeight;
      let closestAspectRatio = '1:1';
      
      // Define Gemini's supported aspect ratios with their numeric values
      const supportedRatios = [
        { ratio: '1:1', value: 1 },
        { ratio: '3:4', value: 3/4 },      // 0.75 - portrait
        { ratio: '4:3', value: 4/3 },      // 1.33 - landscape
        { ratio: '9:16', value: 9/16 },    // 0.5625 - tall portrait
        { ratio: '16:9', value: 16/9 },    // 1.78 - wide landscape
      ];
      
      // Find the closest matching aspect ratio
      let minDiff = Infinity;
      for (const { ratio, value } of supportedRatios) {
        const diff = Math.abs(imageAspectRatio - value);
        if (diff < minDiff) {
          minDiff = diff;
          closestAspectRatio = ratio;
        }
      }
      
      console.log(`📐 Image dimensions: ${img.naturalWidth}x${img.naturalHeight}, aspect ratio: ${imageAspectRatio.toFixed(3)}, closest Gemini ratio: ${closestAspectRatio}`);
      setEditAspectRatio(closestAspectRatio);
    };
    img.onerror = () => {
      // Fallback to canvas-based aspect ratio if image fails to load
      console.warn('Failed to load image for aspect ratio calculation, using default');
      const canvasSize = storybook?.canvasSize || '';
      const isPortrait = canvasSize === '5.5x8.5' || canvasSize === '6x9' || canvasSize === '6.14x9.21' || canvasSize === '7x10' || canvasSize === '8.25x11';
      setEditAspectRatio(isPortrait ? '9:16' : '1:1');
    };
    img.src = getImageUrl(imageUrl);
    
    // Set initial default while image loads
    setEditAspectRatio('1:1');
    setEditCameraAngle('eye-level'); // Default camera angle
    
    setShowEditModal(true);
  };

  const handleEditImage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onEditImage || !editNewName.trim()) return;
    
    // Instead of creating highlighted image on frontend (CORS issues),
    // just send the lasso data to backend and let it create the highlight
    let lassoData: { points: {x: number, y: number}[], mode: string, canvasWidth: number, canvasHeight: number } | undefined = undefined;
    
    if (lassoPoints.length > 2 && canvasRef.current && lassoMode !== 'none') {
      const canvas = canvasRef.current;
      lassoData = {
        points: lassoPoints,
        mode: lassoMode,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height
      };
      console.log('📍 Sending lasso data to backend:', lassoData.points.length, 'points, mode:', lassoData.mode);
      console.log('📐 Canvas dimensions:', canvas.width, 'x', canvas.height);
      console.log('📍 First 5 lasso points:', lassoPoints.slice(0, 5));
    }
    
    // Show generation progress modal
    setGenerationType(editImageType);
    setGenerationItemName(editNewName);
    setGeneratedImageUrl(null);
    setShowGenerationProgress(true);
    
    setIsEditing(true);
    try {
      const newImageUrl = await onEditImage(
        editImageUrl, 
        editPrompt, 
        editNewName, 
        editImageType,
        editBackgroundOption,
        editChromaKeyColor,
        editFromCanvas,
        editCanvasImageId || undefined,
        undefined, // No longer sending highlighted image from frontend
        lassoData,  // Send lasso coordinates instead - backend will create highlight
        editRefImages.length > 0 ? editRefImages : undefined, // Send reference images if any
        editAspectRatio, // Aspect ratio
        editCameraAngle  // Camera angle
      );
      
      // Display the edited image in the progress modal
      if (newImageUrl) {
        setGeneratedImageUrl(newImageUrl);
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setShowEditModal(false);
      setEditPrompt('');
      setEditNewName('');
      setLassoPoints([]);
      lassoPointsRef.current = [];
      setLassoMode('none');
    } catch (error) {
      console.error('Failed to edit image:', error);
      alert('Failed to edit image');
      setShowGenerationProgress(false);
    } finally {
      setIsEditing(false);
    }
  };

  // Canvas upscale function - 2x upscale using browser Canvas API
  const handleDownloadImage = async (imageUrl: string, name: string) => {
    try {
      // For cloud storage URLs, create download link directly
      if (imageUrl.startsWith('https://storage.googleapis.com/')) {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
        link.target = '_blank'; // Open in new tab if download doesn't work
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // For local/server URLs, fetch and download as blob
      const response = await fetch(getImageUrl(imageUrl), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);

      // Fallback: try opening in new tab
      try {
        const fullUrl = imageUrl.startsWith('https://storage.googleapis.com/')
          ? imageUrl
          : getImageUrl(imageUrl);

        const link = document.createElement('a');
        link.href = fullUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        alert('Download failed, opening image in new tab instead.');
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        alert('Failed to download image. Please try again or contact support.');
      }
    }
  };

  // Canvas upscale function - 2x upscale using browser Canvas API
  const handleCanvasUpscale = async (imageUrl: string, imageId: string) => {
    return new Promise<{ dataUrl: string; sizeInMB: number }>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Create canvas at 2x size
        const canvas = document.createElement('canvas');
        canvas.width = img.width * 2;
        canvas.height = img.height * 2;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Use high quality smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw image at 2x size
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert to PNG with 100% quality (lossless)
        const upscaledDataUrl = canvas.toDataURL('image/png');
        
        // Calculate size in MB
        const sizeInBytes = upscaledDataUrl.length * 0.75; // base64 is ~33% larger than binary
        const sizeInMB = sizeInBytes / (1024 * 1024);
        
        resolve({ dataUrl: upscaledDataUrl, sizeInMB });
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for upscaling'));
      };
      
      img.src = getImageUrl(imageUrl);
    });
  };

  return (
    <>
      <div className="sidebar">
        <div className="inspector-tabs" role="tablist" aria-label="Editor inspector">
          <button
            type="button"
            id="editor-properties-tab"
            className="inspector-tab"
            role="tab"
            aria-selected={activeInspectorTab === 'properties'}
            aria-controls="editor-properties-panel"
            tabIndex={activeInspectorTab === 'properties' ? 0 : -1}
            onClick={() => setActiveInspectorTab('properties')}
            onKeyDown={handleInspectorTabKeyDown}
          >
            Properties
          </button>
          <button
            type="button"
            id="editor-assets-tab"
            className="inspector-tab"
            role="tab"
            aria-selected={activeInspectorTab === 'assets'}
            aria-controls="editor-assets-panel"
            tabIndex={activeInspectorTab === 'assets' ? 0 : -1}
            onClick={() => setActiveInspectorTab('assets')}
            onKeyDown={handleInspectorTabKeyDown}
          >
            Assets
          </button>
        </div>

        <section
          id="editor-properties-panel"
          className="inspector-tab-panel inspector-properties-panel"
          role="tabpanel"
          aria-labelledby="editor-properties-tab"
          hidden={activeInspectorTab !== 'properties'}
        >
          {/* Element Editing Toolbar - Shows when element is selected */}
          {selectedElement && currentPage && (
            <div className="element-toolbar">
            <h3 className="sidebar-title">
              {currentPage.textElements.find(el => el.id === selectedElement) ? '✏️ Text Properties' : '🖼️ Image Properties'}
            </h3>
            
            {/* Text Element Controls */}
            {currentPage.textElements.find(el => el.id === selectedElement) && (() => {
              const selectedText = currentPage.textElements.find(el => el.id === selectedElement)!;
              return (
                <div className="element-controls">
                  {/* Font Settings - Size & Family grouped */}
                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Font Size</label>
                      <input
                        type="number"
                        className="control-input"
                        value={selectedText.fontSize}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { fontSize: parseInt(e.target.value) })}
                        min="8"
                        max="120"
                      />
                    </div>

                    <div className="control-group">
                      <label className="control-label">Font Family</label>
                      <select
                        className="control-input"
                        value={selectedText.fontFamily}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { fontFamily: e.target.value })}
                        style={{ fontFamily: selectedText.fontFamily }}
                      >
                        <optgroup label="Sans Serif">
                          <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                          <option value="Open Sans" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
                          <option value="Lato" style={{ fontFamily: 'Lato' }}>Lato</option>
                          <option value="Montserrat" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
                          <option value="Raleway" style={{ fontFamily: 'Raleway' }}>Raleway</option>
                          <option value="Ubuntu" style={{ fontFamily: 'Ubuntu' }}>Ubuntu</option>
                          <option value="Oswald" style={{ fontFamily: 'Oswald' }}>Oswald</option>
                        </optgroup>
                        <optgroup label="Serif">
                          <option value="Merriweather" style={{ fontFamily: 'Merriweather' }}>Merriweather</option>
                          <option value="Playfair Display" style={{ fontFamily: 'Playfair Display' }}>Playfair Display</option>
                        </optgroup>
                        <optgroup label="Handwriting & Fun">
                          <option value="Patrick Hand" style={{ fontFamily: 'Patrick Hand' }}>Patrick Hand (Comic Style)</option>
                          <option value="Indie Flower" style={{ fontFamily: 'Indie Flower' }}>Indie Flower</option>
                          <option value="Shadows Into Light" style={{ fontFamily: 'Shadows Into Light' }}>Shadows Into Light</option>
                          <option value="Caveat" style={{ fontFamily: 'Caveat' }}>Caveat</option>
                          <option value="Architects Daughter" style={{ fontFamily: 'Architects Daughter' }}>Architects Daughter</option>
                          <option value="Kalam" style={{ fontFamily: 'Kalam' }}>Kalam</option>
                          <option value="Permanent Marker" style={{ fontFamily: 'Permanent Marker' }}>Permanent Marker</option>
                        </optgroup>
                        <optgroup label="Elegant & Decorative">
                          <option value="Dancing Script" style={{ fontFamily: 'Dancing Script' }}>Dancing Script</option>
                          <option value="Pacifico" style={{ fontFamily: 'Pacifico' }}>Pacifico</option>
                          <option value="Playfair Display" style={{ fontFamily: 'Playfair Display' }}>Playfair Display</option>
                          <option value="Merriweather" style={{ fontFamily: 'Merriweather' }}>Merriweather</option>
                        </optgroup>
                        <optgroup label="Bold & Strong">
                          <option value="Bebas Neue" style={{ fontFamily: 'Bebas Neue' }}>Bebas Neue</option>
                          <option value="Bangers" style={{ fontFamily: 'Bangers' }}>Bangers</option>
                          <option value="Fredoka One" style={{ fontFamily: 'Fredoka One' }}>Fredoka One</option>
                          <option value="Righteous" style={{ fontFamily: 'Righteous' }}>Righteous</option>
                          <option value="Audiowide" style={{ fontFamily: 'Audiowide' }}>Audiowide</option>
                          <option value="Press Start 2P" style={{ fontFamily: 'Press Start 2P' }}>Press Start 2P</option>
                        </optgroup>
                        <optgroup label="Modern & Clean">
                          <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                          <option value="Open Sans" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
                          <option value="Lato" style={{ fontFamily: 'Lato' }}>Lato</option>
                          <option value="Montserrat" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
                          <option value="Raleway" style={{ fontFamily: 'Raleway' }}>Raleway</option>
                          <option value="Ubuntu" style={{ fontFamily: 'Ubuntu' }}>Ubuntu</option>
                          <option value="Oswald" style={{ fontFamily: 'Oswald' }}>Oswald</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  {/* Font Weight & Text Color grouped */}
                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Font Weight</label>
                      <select
                        className="control-input"
                        value={selectedText.fontWeight}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { fontWeight: e.target.value })}
                      >
                        <option value="100">Thin (100)</option>
                        <option value="200">Extra Light (200)</option>
                        <option value="300">Light (300)</option>
                        <option value="normal">Normal (400)</option>
                        <option value="500">Medium (500)</option>
                        <option value="600">Semi Bold (600)</option>
                        <option value="bold">Bold (700)</option>
                        <option value="800">Extra Bold (800)</option>
                        <option value="900">Black (900)</option>
                      </select>
                    </div>

                    <div className="control-group">
                      <label className="control-label">Text Color</label>
                      <input
                        type="color"
                        className="control-input"
                        value={toHexColor(selectedText.color, '#000000')}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { color: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Text Style: Italic, Underline, Strikethrough */}
                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Font Style</label>
                      <select
                        className="control-input"
                        value={selectedText.fontStyle || 'normal'}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { fontStyle: e.target.value as 'normal' | 'italic' })}
                      >
                        <option value="normal">Normal</option>
                        <option value="italic">Italic</option>
                      </select>
                    </div>

                    <div className="control-group">
                      <label className="control-label">Text Decoration</label>
                      <select
                        className="control-input"
                        value={selectedText.textDecoration || 'none'}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { textDecoration: e.target.value as any })}
                      >
                        <option value="none">None</option>
                        <option value="underline">Underline</option>
                        <option value="line-through">Strikethrough</option>
                        <option value="underline line-through">Both</option>
                      </select>
                    </div>
                  </div>

                  {/* 3D Title Style Colors - Only show if 3D Title is selected */}
                  {selectedText.styleId === '3d-title' && (
                    <>
                      <div className="control-row">
                        <div className="control-group">
                          <label className="control-label">Shadow Color 1</label>
                          <input
                            type="color"
                            className="control-input"
                            value={toHexColor(selectedText.title3dColor1, '#4af7ff')}
                            onChange={(e) => onUpdateTextElement?.(selectedText.id, { title3dColor1: e.target.value })}
                          />
                        </div>

                        <div className="control-group">
                          <label className="control-label">Shadow Color 2</label>
                          <input
                            type="color"
                            className="control-input"
                            value={toHexColor(selectedText.title3dColor2, '#165bfb')}
                            onChange={(e) => onUpdateTextElement?.(selectedText.id, { title3dColor2: e.target.value })}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Text Align & Vertical Align grouped */}
                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Text Align</label>
                      <select
                        className="control-input"
                        value={selectedText.textAlign || 'center'}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { textAlign: e.target.value as any })}
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>

                    <div className="control-group">
                      <label className="control-label">Vertical Align</label>
                      <select
                        className="control-input"
                        value={selectedText.verticalAlign || 'top'}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { verticalAlign: e.target.value as any })}
                      >
                        <option value="top">Top</option>
                        <option value="center">Center</option>
                        <option value="bottom">Bottom</option>
                      </select>
                    </div>
                  </div>

                  {/* Size */}
                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Width</label>
                      <input
                        type="number"
                        className="control-input"
                        value={Math.round(selectedText.width)}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { width: Math.max(50, parseInt(e.target.value) || 50) })}
                        min="50"
                      />
                    </div>
                    <div className="control-group">
                      <label className="control-label">Height</label>
                      <input
                        type="number"
                        className="control-input"
                        value={Math.round(selectedText.height)}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { height: Math.max(30, parseInt(e.target.value) || 30) })}
                        min="30"
                      />
                    </div>
                  </div>

                  <div className="control-group">
                    <label className="control-label">Rotation</label>
                    <div>
                      <input
                        type="range"
                        className="control-input"
                        value={selectedText.rotation}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { rotation: parseInt(e.target.value) })}
                        min="-180"
                        max="180"
                      />
                      <span className="control-value">{selectedText.rotation}°</span>
                    </div>
                  </div>

                  {/* Background - Bg Color & Bg Opacity grouped */}
                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Bg Color</label>
                      <input
                        type="color"
                        className="control-input"
                        value={toHexColor(selectedText.backgroundColor, '#ffffff')}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { backgroundColor: e.target.value })}
                      />
                    </div>

                    <div className="control-group">
                      <label className="control-label">Bg Opacity</label>
                      <div>
                        <input
                          type="range"
                          className="control-input"
                          value={(selectedText.backgroundOpacity ?? 1) * 100}
                          onChange={(e) => onUpdateTextElement?.(selectedText.id, { backgroundOpacity: parseInt(e.target.value) / 100 })}
                          min="0"
                          max="100"
                        />
                        <span className="control-value">{Math.round((selectedText.backgroundOpacity ?? 1) * 100)}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Border - Border Color separate */}
                  <div className="control-group">
                    <label className="control-label">Border Color</label>
                    <input
                      type="color"
                      className="control-input"
                      value={toHexColor(selectedText.borderColor, '#000000')}
                      onChange={(e) => onUpdateTextElement?.(selectedText.id, { borderColor: e.target.value })}
                    />
                  </div>

                  {/* Border Width & Border Style grouped */}
                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Border Width</label>
                      <input
                        type="number"
                        className="control-input"
                        value={selectedText.borderWidth || 0}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { borderWidth: parseInt(e.target.value) })}
                        min="0"
                        max="20"
                      />
                    </div>

                    <div className="control-group">
                      <label className="control-label">Border Style</label>
                      <select
                        className="control-input"
                        value={selectedText.borderStyle || 'solid'}
                        onChange={(e) => onUpdateTextElement?.(selectedText.id, { borderStyle: e.target.value })}
                      >
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                        <option value="double">Double</option>
                      </select>
                    </div>
                  </div>

                  {/* Tear Box Intensity - Only show for tear box style */}
                  {selectedText.styleId === 'tear-box' && (
                    <div className="control-group">
                      <label className="control-label">Tear Intensity</label>
                      <div>
                        <input
                          type="range"
                          className="control-input"
                          value={selectedText.tearBoxIntensity ?? 50}
                          onChange={(e) => onUpdateTextElement?.(selectedText.id, { tearBoxIntensity: parseInt(e.target.value) })}
                          min="0"
                          max="100"
                        />
                        <span className="control-value">{selectedText.tearBoxIntensity ?? 50}</span>
                      </div>
                    </div>
                  )}

                  {/* Speech Bubble Direction - Only show for speech bubble styles */}
                  {selectedText.styleId && (
                    selectedText.styleId === 'speech-bubble' || 
                    selectedText.styleId === 'speech-bubble-right' ||
                    selectedText.styleId === 'shout-bubble-left' ||
                    selectedText.styleId === 'shout-bubble-right' ||
                    selectedText.styleId === 'whisper-bubble-left' ||
                    selectedText.styleId === 'whisper-bubble-right' ||
                    selectedText.styleId === 'rectangular-bubble-left' ||
                    selectedText.styleId === 'rectangular-bubble-right' ||
                    selectedText.styleId === 'thought-bubble-left' ||
                    selectedText.styleId === 'thought-bubble-right' ||
                    selectedText.styleId === 'ellipse-bubble-left' ||
                    selectedText.styleId === 'ellipse-bubble-right'
                  ) && (
                    <div className="control-group">
                      <label className="control-label">Bubble Direction</label>
                      <div className="bubble-direction-toggle">
                        <button
                          className={`bubble-dir-btn ${
                            selectedText.styleId === 'speech-bubble' ||
                            selectedText.styleId === 'shout-bubble-left' ||
                            selectedText.styleId === 'whisper-bubble-left' ||
                            selectedText.styleId === 'rectangular-bubble-left' ||
                            selectedText.styleId === 'thought-bubble-left' ||
                            selectedText.styleId === 'ellipse-bubble-left'
                              ? 'active' : ''
                          }`}
                          onClick={() => {
                            // Switch to left variant
                            const currentId = selectedText.styleId || '';
                            let newId = currentId;
                            if (currentId === 'speech-bubble-right') newId = 'speech-bubble';
                            else if (currentId.endsWith('-right')) newId = currentId.replace('-right', '-left');
                            onUpdateTextElement?.(selectedText.id, { styleId: newId });
                          }}
                        >
                          ◀ Left
                        </button>
                        <button
                          className={`bubble-dir-btn ${
                            selectedText.styleId === 'speech-bubble-right' ||
                            selectedText.styleId === 'shout-bubble-right' ||
                            selectedText.styleId === 'whisper-bubble-right' ||
                            selectedText.styleId === 'rectangular-bubble-right' ||
                            selectedText.styleId === 'thought-bubble-right' ||
                            selectedText.styleId === 'ellipse-bubble-right'
                              ? 'active' : ''
                          }`}
                          onClick={() => {
                            // Switch to right variant
                            const currentId = selectedText.styleId || '';
                            let newId = currentId;
                            if (currentId === 'speech-bubble') newId = 'speech-bubble-right';
                            else if (currentId.endsWith('-left')) newId = currentId.replace('-left', '-right');
                            onUpdateTextElement?.(selectedText.id, { styleId: newId });
                          }}
                        >
                          Right ▶
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {onRephraseText && (
                    <button 
                      className="btn btn-primary btn-small control-full"
                      onClick={() => onRephraseText(selectedText.id, selectedText.content)}
                      disabled={isRephrasing}
                      style={{ marginTop: '8px' }}
                    >
                      {isRephrasing ? '✨ Improving...' : '✨ AI Improve Text'}
                    </button>
                  )}

                  <button 
                    className="btn btn-secondary btn-small control-full"
                    onClick={onDeleteElement}
                    style={{ marginTop: '8px' }}
                  >
                    🗑️ Delete Text
                  </button>
                </div>
              );
            })()}

            {/* Image Element Controls */}
            {currentPage.imageElements.find(el => el.id === selectedElement) && (() => {
              const selectedImage = currentPage.imageElements.find(el => el.id === selectedElement)!;
              
              // Initialize aspect ratio if not set
              if (imageAspectRatio === null) {
                setImageAspectRatio(selectedImage.width / selectedImage.height);
              }

              const handleWidthChange = (newWidth: number) => {
                if (aspectRatioLocked && imageAspectRatio) {
                  const newHeight = newWidth / imageAspectRatio;
                  onUpdateImageElement?.(selectedImage.id, { width: newWidth, height: newHeight });
                } else {
                  onUpdateImageElement?.(selectedImage.id, { width: newWidth });
                }
              };

              const handleHeightChange = (newHeight: number) => {
                if (aspectRatioLocked && imageAspectRatio) {
                  const newWidth = newHeight * imageAspectRatio;
                  onUpdateImageElement?.(selectedImage.id, { width: newWidth, height: newHeight });
                } else {
                  onUpdateImageElement?.(selectedImage.id, { height: newHeight });
                }
              };

              return (
                <div className="element-controls">
                  <div className="control-group">
                    <label className="control-label" htmlFor={`image-alt-text-${selectedImage.id}`}>
                      Alternative text
                    </label>
                    <textarea
                      id={`image-alt-text-${selectedImage.id}`}
                      className="control-input"
                      rows={3}
                      maxLength={500}
                      value={selectedImage.altText || ''}
                      aria-describedby={`image-alt-text-help-${selectedImage.id}`}
                      placeholder="Describe what matters in this illustration"
                      onChange={(event) => onUpdateImageElement?.(selectedImage.id, {
                        altText: event.target.value
                      })}
                    />
                    <span
                      id={`image-alt-text-help-${selectedImage.id}`}
                      className="control-help"
                    >
                      Describe the meaningful visual details; leave empty only for decoration.
                    </span>
                  </div>

                  <div className="control-row">
                    <div className="control-group">
                      <label className="control-label">Width</label>
                      <input
                        type="number"
                        className="control-input"
                        value={Math.round(selectedImage.width)}
                        onChange={(e) => handleWidthChange(parseInt(e.target.value))}
                        min="50"
                      />
                    </div>

                    <div className="control-group">
                      <label className="control-label">Height</label>
                      <input
                        type="number"
                        className="control-input"
                        value={Math.round(selectedImage.height)}
                        onChange={(e) => handleHeightChange(parseInt(e.target.value))}
                        min="50"
                      />
                    </div>
                  </div>

                  <div className="control-group">
                    <label className="control-label">Rotation</label>
                    <input
                      type="range"
                      className="control-input"
                      value={selectedImage.rotation}
                      onChange={(e) => onUpdateImageElement?.(selectedImage.id, { rotation: parseInt(e.target.value) })}
                      min="-180"
                      max="180"
                    />
                    <span className="control-value">{selectedImage.rotation}°</span>
                  </div>

                  <div className="control-group" style={{ gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button 
                      className="btn btn-secondary btn-small"
                      onClick={() => onUpdateImageElement?.(selectedImage.id, { flipHorizontal: !selectedImage.flipHorizontal })}
                      style={{ 
                        width: '100%',
                        backgroundColor: selectedImage.flipHorizontal ? '#3B82F6' : undefined,
                        color: selectedImage.flipHorizontal ? 'white' : undefined
                      }}
                      title="Flip horizontally"
                    >
                      ↔️ Flip H
                    </button>
                    <button 
                      className="btn btn-secondary btn-small"
                      onClick={() => onUpdateImageElement?.(selectedImage.id, { flipVertical: !selectedImage.flipVertical })}
                      style={{ 
                        width: '100%',
                        backgroundColor: selectedImage.flipVertical ? '#3B82F6' : undefined,
                        color: selectedImage.flipVertical ? 'white' : undefined
                      }}
                      title="Flip vertically"
                    >
                      ↕️ Flip V
                    </button>
                  </div>

                  {/* Crop and Copy buttons - 2 per row */}
                  {(onCropImage || onSaveImageToScene) && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                      {onCropImage && (
                        <button 
                          className="btn btn-primary btn-small"
                          onClick={onCropImage}
                          style={{ flex: '1 1 calc(50% - 4px)' }}
                        >
                          ✂️ Crop Image
                        </button>
                      )}

                      {onSaveImageToScene && (
                        <button 
                          className="btn btn-primary btn-small"
                          onClick={() => onSaveImageToScene(selectedImage.url)}
                          style={{ flex: '1 1 calc(50% - 4px)' }}
                        >
                          💾 Save as a copy
                        </button>
                      )}
                    </div>
                  )}

                  {/* Button Grid - 2 buttons per row */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {onUpscaleImage && (
                      <div style={{ flex: '1 1 calc(50% - 4px)', position: 'relative' }}>
                        <button 
                          className="btn btn-primary btn-small"
                          onClick={() => setShowUpscaleDropdown(!showUpscaleDropdown)}
                          disabled={isUpscaling}
                          style={{ width: '100%' }}
                        >
                          {isUpscaling ? '🔍 Upscaling...' : '🔍 AI Upscale ▾'}
                        </button>
                        {showUpscaleDropdown && !isUpscaling && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            background: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 100,
                            marginTop: '4px',
                            overflow: 'hidden'
                          }}>
                            {(['x2', 'x3', 'x4'] as const).map(factor => (
                              <button
                                key={factor}
                                className="btn btn-small"
                                style={{
                                  width: '100%',
                                  borderRadius: 0,
                                  borderBottom: factor !== 'x4' ? '1px solid #F3F4F6' : 'none',
                                  justifyContent: 'flex-start',
                                  padding: '8px 12px',
                                  fontSize: '13px',
                                  background: 'white',
                                  color: '#374151',
                                  cursor: 'pointer'
                                }}
                                onClick={async () => {
                                  setShowUpscaleDropdown(false);
                                  if (!window.confirm(`Upscale this image ${factor}?\n\nCost: 10 tokens\nThis will use AI tokens from your account.`)) return;
                                  setIsUpscaling(true);
                                  try {
                                    const oldUrl = selectedImage.url;
                                    const upscaledUrl = await onUpscaleImage(selectedImage.url, factor);
                                    onUpdateImageElement?.(selectedImage.id, {
                                      url: upscaledUrl,
                                      naturalWidth: undefined,
                                      naturalHeight: undefined,
                                      upscaleCount: (selectedImage.upscaleCount || 0) + 1,
                                      imageHistory: [
                                        ...(selectedImage.imageHistory || []),
                                        {
                                          action: 'upscaled',
                                          timestamp: new Date().toISOString(),
                                          previousUrl: oldUrl,
                                          newUrl: upscaledUrl,
                                          details: `Upscaled ${factor} using Imagen 4`,
                                          cost: 10
                                        }
                                      ]
                                    });
                                    // Also update the gallery entry (character/asset/background/scene)
                                    onUpdateGalleryImage?.(oldUrl, upscaledUrl);
                                    alert('Image upscaled successfully!');
                                  } catch (error) {
                                    alert('Failed to upscale image');
                                  } finally {
                                    setIsUpscaling(false);
                                  }
                                }}
                              >
                                🔍 {factor} Upscale (10 tokens)
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {onShowImageHistory && (
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => onShowImageHistory(selectedImage)}
                        style={{ flex: '1 1 calc(50% - 4px)' }}
                      >
                        📋 History
                      </button>
                    )}

                    <button 
                      className="btn btn-primary btn-small"
                      onClick={async () => {
                        setIsApplyingTransparency(true);
                        setShowTransparencyLoadingModal(true);
                        try {
                          // Determine image category (asset, character, background, or scene)
                          let imageCategory: 'asset' | 'character' | 'background' | 'scene' = 'scene';
                          let originalName = selectedImage.characterName || 'Image';
                          
                          if (storybook) {
                            // Find the image in the galleries to get its category and name
                            const character = storybook.characters.find(c => c.imageUrl === selectedImage.url);
                            if (character) {
                              imageCategory = 'character';
                              originalName = character.name;
                            } else {
                              const asset = storybook.assets?.find(a => a.imageUrl === selectedImage.url);
                              if (asset) {
                                imageCategory = 'asset';
                                originalName = asset.name;
                              } else {
                                const background = storybook.backgrounds?.find(b => b.imageUrl === selectedImage.url);
                                if (background) {
                                  imageCategory = 'background';
                                  originalName = background.name;
                                } else {
                                  const scene = storybook.scenes?.find(s => s.imageUrl === selectedImage.url);
                                  if (scene) {
                                    imageCategory = 'scene';
                                    originalName = scene.name;
                                  }
                                }
                              }
                            }
                          }
                          
                          // Step 1: Analyze the image to find the safest chroma key color
                          setTransparencyLoadingMessage('🤖 AI is analyzing your image...');
                          console.log('🔍 Step 1: Analyzing image for safest chroma key color...');
                          const analysisResult = await aiAPI.analyzeChromaColor(selectedImage.url);
                          
                          if (analysisResult.success && analysisResult.chromaKey) {
                            const chromaKey = analysisResult.chromaKey;
                            const { r, g, b, colorName, percentage } = chromaKey;
                            console.log(`✅ Detected safest color: ${colorName} RGB(${r}, ${g}, ${b}) - ${percentage.toFixed(2)}% used`);
                            
                            // Step 2 & 3: Regenerate with chroma key and remove background
                            setTransparencyLoadingMessage('🎨 AI is drawing a fresh copy...');
                            console.log(`🎨 Step 2: Regenerating image with ${colorName} background using Gemini...`);
                            console.log(`📂 Image category: ${imageCategory}, Name: ${originalName}`);
                            const result = await aiAPI.applyTransparency(
                              selectedImage.url, 
                              15, 
                              chromaKey, 
                              imageCategory,
                              storybook?._id,
                              originalName
                            );
                            
                            if (result.success && result.imageUrl) {
                              setTransparencyLoadingMessage('✨ AI is removing the background...');
                              console.log(`✅ AI Transparency complete! Regenerated with ${colorName} background and removed it.`);
                              console.log(`📝 New image name: ${result.imageName}`);
                              
                              // Load the transparent image to get its natural dimensions
                              const loadImageDimensions = (): Promise<{ width: number; height: number; aspectRatio: number }> => {
                                return new Promise((resolve, reject) => {
                                  const img = new Image();
                                  img.onload = () => {
                                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                                    console.log(`📐 New transparent image dimensions: ${img.naturalWidth}x${img.naturalHeight}, aspect ratio: ${aspectRatio.toFixed(3)}`);
                                    resolve({ 
                                      width: img.naturalWidth, 
                                      height: img.naturalHeight, 
                                      aspectRatio 
                                    });
                                  };
                                  img.onerror = () => reject(new Error('Failed to load image'));
                                  img.src = result.imageUrl;
                                });
                              };

                              try {
                                // Get new image dimensions
                                const { aspectRatio: newAspectRatio } = await loadImageDimensions();
                                
                                // Update the aspect ratio for the image controls
                                setImageAspectRatio(newAspectRatio);
                                
                                // Unlock aspect ratio so user can adjust freely if needed
                                setAspectRatioLocked(false);
                                
                                // Calculate new height based on current width and new aspect ratio
                                const newHeight = selectedImage.width / newAspectRatio;
                                
                                // Update the canvas image with transparent version
                                onUpdateImageElement?.(selectedImage.id, { 
                                  url: result.imageUrl,
                                  width: selectedImage.width,
                                  height: newHeight,
                                  x: selectedImage.x,
                                  y: selectedImage.y,
                                  rotation: selectedImage.rotation,
                                  imageHistory: [
                                    ...(selectedImage.imageHistory || []),
                                    {
                                      action: 'transparency',
                                      timestamp: new Date().toISOString(),
                                      previousUrl: selectedImage.url,
                                      newUrl: result.imageUrl,
                                      details: 'AI Transparency: Background removed'
                                    }
                                  ]
                                });
                                
                                console.log(`✅ Canvas image updated. New transparent image is in gallery: ${result.imageName}`);
                                
                                // NOTE: We don't call onRefreshStorybook here because it would reload the entire
                                // storybook and reset the current page, making the image disappear.
                                // The new transparent image is already in the gallery (added by backend).
                                // Users will see it when they switch tabs or the gallery auto-refreshes.
                              } catch (error) {
                                console.error('Failed to load transparent image dimensions:', error);
                                // Fallback: just update URL without changing dimensions
                                onUpdateImageElement?.(selectedImage.id, { 
                                  url: result.imageUrl,
                                  width: selectedImage.width,
                                  height: selectedImage.height,
                                  x: selectedImage.x,
                                  y: selectedImage.y,
                                  rotation: selectedImage.rotation,
                                  imageHistory: [
                                    ...(selectedImage.imageHistory || []),
                                    {
                                      action: 'transparency',
                                      timestamp: new Date().toISOString(),
                                      previousUrl: selectedImage.url,
                                      newUrl: result.imageUrl,
                                      details: 'AI Transparency: Background removed'
                                    }
                                  ]
                                });
                              }
                              
                              // Show success briefly before closing
                              setTimeout(() => {
                                setTransparencyLoadingMessage('✅ All done! Background removed perfectly.');
                                setTimeout(() => {
                                  setShowTransparencyLoadingModal(false);
                                }, 1000);
                              }, 500);
                            }
                          } else {
                            throw new Error('Failed to analyze chroma key color');
                          }
                        } catch (error: any) {
                          console.error('❌ Apply transparency error:', error);
                          console.error('Error details:', error.response?.data?.error || error.message);
                          setTransparencyLoadingMessage('❌ Oops! Something went wrong: ' + (error.response?.data?.error || error.message || 'Failed to apply transparency'));
                          setTimeout(() => {
                            setShowTransparencyLoadingModal(false);
                          }, 3000);
                        } finally {
                          setIsApplyingTransparency(false);
                        }
                      }}
                      disabled={isApplyingTransparency}
                      style={{ flex: '1 1 calc(50% - 4px)' }}
                      title="Use AI to regenerate image with chroma key background and remove it for perfect transparency"
                    >
                      {isApplyingTransparency ? '🎨 Processing AI...' : '🎨 AI Transparency'}
                    </button>

                    {onEditImage && (
                      <button 
                        className="btn btn-primary btn-small"
                        onClick={() => {
                          const imageName = selectedImage.characterName || 'Image';
                          let imageType: 'asset' | 'character' | 'background' | 'scene' = 'scene';
                          
                          if (storybook) {
                            if (storybook.characters.some(c => c.imageUrl === selectedImage.url)) {
                              imageType = 'character';
                            }
                            else if (storybook.assets?.some(a => a.imageUrl === selectedImage.url)) {
                              imageType = 'asset';
                            }
                            else if (storybook.backgrounds?.some(b => b.imageUrl === selectedImage.url)) {
                              imageType = 'background';
                            }
                            else if (storybook.scenes?.some(s => s.imageUrl === selectedImage.url)) {
                              imageType = 'scene';
                            }
                          }
                          
                          handleOpenEditModal(selectedImage.url, imageName, imageType, selectedImage.prompt, true, selectedImage.id);
                        }}
                        style={{ flex: '1 1 calc(50% - 4px)' }}
                      >
                        ✏️ Edit Image
                      </button>
                    )}

                    <button 
                      className="btn btn-secondary btn-small"
                      onClick={onDeleteElement}
                      style={{ flex: '1 1 calc(50% - 4px)' }}
                    >
                      🗑️ Delete Image
                    </button>
                  </div>

                  {/* Layer Controls */}
                  <div style={{ marginTop: '12px' }}>
                    <label className="control-label" style={{ marginBottom: '8px' }}>Layer Order</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() => onBringToFront?.(selectedImage.id, 'image')}
                        style={{ flex: 1 }}
                        title="Bring to Front"
                      >
                        ⬆️ Bring to Front
                      </button>
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() => onSendToBack?.(selectedImage.id, 'image')}
                        style={{ flex: 1 }}
                        title="Send to Back"
                      >
                        ⬇️ Send to Back
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
            </div>
          )}
          {(!selectedElement || !currentPage) && (
            <div className="inspector-empty-state">
              <span aria-hidden="true">↖</span>
              <h3>No element selected</h3>
              <p>Select text or an image on the page to edit its properties.</p>
            </div>
          )}
        </section>

        <section
          id="editor-assets-panel"
          className="inspector-tab-panel inspector-assets-panel"
          role="tabpanel"
          aria-labelledby="editor-assets-tab"
          hidden={activeInspectorTab !== 'assets'}
        >
          {/* Gallery Button */}
          <div className="gallery-section">
          <button 
            className="btn btn-gallery"
            onClick={() => setShowGalleryModal(true)}
          >
            Open Gallery
          </button>
        </div>

        <div className="sidebar-columns">
          {/* Assets Column */}
          <div className="sidebar-section">
            <h3 className="sidebar-title">Assets</h3>
            
            <button 
              className="btn btn-primary btn-small"
              onClick={() => setShowAssetModal(true)}
            >
              + Generate
            </button>

            <label className="btn btn-secondary btn-small upload-btn">
              Upload
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadAsset}
                style={{ display: 'none' }}
              />
            </label>

            <div className="character-gallery">
              {(storybook.assets || []).length === 0 ? (
                <p className="empty-message">No assets yet</p>
              ) : (
                (storybook.assets || []).map((asset, index) => (
                  <div
                    key={`asset-${index}-${asset.imageUrl || asset.name}`}
                    className="character-card"
                    draggable={!!asset.imageUrl}
                    onDragStart={(e) => {
                      if (asset.imageUrl) {
                        handleDragStart(e, asset.imageUrl, asset.name, asset.prompt);
                        handleDeleteDragStart(e, 'asset', asset.name, asset.imageUrl);
                      }
                    }}
                    onDragEnd={handleDeleteDragEnd}
                    onClick={() => asset.imageUrl && onAddImage(asset.imageUrl, undefined, undefined, asset.prompt, asset.name)}
                    style={{ cursor: asset.imageUrl ? 'grab' : 'default' }}
                    title={asset.prompt ? `${asset.name}\n\nPrompt: ${asset.prompt}` : asset.name}
                  >
                    {asset.imageUrl ? (
                      <>
                        <img src={getImageUrl(getThumbnailOrFullUrl(asset.imageUrl, asset.thumbnailUrl, true, 'asset', asset.name)!)} alt={asset.name} draggable={false} />
                        {hasApiAccess && onEditImage && (
                          <button
                            className="edit-image-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(asset.imageUrl!, asset.name, 'asset', asset.prompt);
                            }}
                            title="Edit image with AI"
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          className="download-image-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadImage(asset.imageUrl!, asset.name);
                          }}
                          title="Download image"
                        >
                          ⬇️
                        </button>
                      </>
                    ) : (
                      <div className="character-placeholder">Generating...</div>
                    )}
                    <p className="character-name" title={asset.prompt ? `Prompt: ${asset.prompt}` : asset.name}>{asset.name}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Characters Column */}
          <div className="sidebar-section">
            <h3 className="sidebar-title">Characters</h3>
            
            <button 
              className="btn btn-primary btn-small"
              onClick={() => setShowCharacterModal(true)}
            >
              + Generate
            </button>

            <label className="btn btn-secondary btn-small upload-btn">
              Upload
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadCharacter}
                style={{ display: 'none' }}
              />
            </label>

            <div className="character-gallery">
              {storybook.characters.length === 0 ? (
                <p className="empty-message">No characters yet</p>
              ) : (
                storybook.characters.map((char, index) => (
                  <div
                    key={`char-${index}-${char.imageUrl || char.name}`}
                    className="character-card"
                    draggable={!!char.imageUrl}
                    onDragStart={(e) => {
                      if (char.imageUrl) {
                        handleDragStart(e, char.imageUrl, char.name, char.prompt);
                        handleDeleteDragStart(e, 'character', char.name, char.imageUrl);
                      }
                    }}
                    onDragEnd={handleDeleteDragEnd}
                    onClick={() => char.imageUrl && onAddImage(char.imageUrl, undefined, undefined, char.prompt, char.name)}
                    style={{ cursor: char.imageUrl ? 'grab' : 'default' }}
                    title={char.prompt ? `${char.name}\n\nPrompt: ${char.prompt}` : char.name}
                  >
                    {char.imageUrl ? (
                      <>
                        <img src={getImageUrl(getThumbnailOrFullUrl(char.imageUrl, char.thumbnailUrl, true, 'character', char.name)!)} alt={char.name} draggable={false} />
                        {hasApiAccess && onEditImage && (
                          <button
                            className="edit-image-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(char.imageUrl!, char.name, 'character', char.prompt);
                            }}
                            title="Edit image with AI"
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          className="download-image-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadImage(char.imageUrl!, char.name);
                          }}
                          title="Download image"
                        >
                          ⬇️
                        </button>
                      </>
                    ) : (
                      <div className="character-placeholder">Generating...</div>
                    )}
                    <p className="character-name" title={char.prompt ? `Prompt: ${char.prompt}` : char.name}>{char.name}</p>
                    {onUpdateCharacterHeight && (
                      <select
                        className="character-height-select"
                        value={char.heightCategory || 'medium'}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          onUpdateCharacterHeight(index, e.target.value as any);
                        }}
                        title="Character size for scene composition"
                      >
                        <option value="tiny">Tiny</option>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="tall">Tall</option>
                        <option value="very-tall">Very Tall</option>
                      </select>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Backgrounds Column */}
          <div className="sidebar-section">
            <h3 className="sidebar-title">Backgrounds</h3>
            
            <button 
              className="btn btn-primary btn-small"
              onClick={() => setShowBackgroundModal(true)}
            >
              + Generate
            </button>

            <label className="btn btn-secondary btn-small upload-btn">
              Upload
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadBackground}
                style={{ display: 'none' }}
              />
            </label>

            <div className="character-gallery">
              {(storybook.backgrounds || []).length === 0 ? (
                <p className="empty-message">No backgrounds yet</p>
              ) : (
                (storybook.backgrounds || []).map((bg, index) => (
                  <div
                    key={`bg-${index}-${bg.imageUrl || bg.name}`}
                    className="character-card"
                    draggable={!!bg.imageUrl}
                    onDragStart={(e) => {
                      if (bg.imageUrl) {
                        handleDragStart(e, bg.imageUrl, bg.name, bg.prompt);
                        handleDeleteDragStart(e, 'background', bg.name, bg.imageUrl);
                      }
                    }}
                    onDragEnd={handleDeleteDragEnd}
                    onClick={() => bg.imageUrl && onAddImage(bg.imageUrl, undefined, undefined, bg.prompt, bg.name)}
                    style={{ cursor: bg.imageUrl ? 'grab' : 'default' }}
                    title={bg.prompt ? `${bg.name}\n\nPrompt: ${bg.prompt}` : bg.name}
                  >
                    {bg.imageUrl ? (
                      <>
                        <img src={getImageUrl(getThumbnailOrFullUrl(bg.imageUrl, bg.thumbnailUrl, true, 'background', bg.name)!)} alt={bg.name} draggable={false} />
                        {hasApiAccess && onEditImage && (
                          <button
                            className="edit-image-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(bg.imageUrl!, bg.name, 'background', bg.prompt);
                            }}
                            title="Edit image with AI"
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          className="download-image-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadImage(bg.imageUrl!, bg.name);
                          }}
                          title="Download image"
                        >
                          ⬇️
                        </button>
                      </>
                    ) : (
                      <div className="character-placeholder">Generating...</div>
                    )}
                    <p className="character-name" title={bg.prompt ? `Prompt: ${bg.prompt}` : bg.name}>{bg.name}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Scenes Column */}
          <div className="sidebar-section">
            <h3 className="sidebar-title">Scenes</h3>
            
            <button 
              className="btn btn-primary btn-small"
              onClick={() => setShowSceneModal(true)}
              disabled={storybook.characters.length === 0}
            >
              + Compose
            </button>

            <label className="btn btn-secondary btn-small upload-btn">
              Upload
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadScene}
                style={{ display: 'none' }}
              />
            </label>

            <div className="character-gallery">
              {(storybook.scenes || []).length === 0 ? (
                <p className="empty-message">
                  {storybook.characters.length === 0 
                    ? 'Create assets first' 
                    : 'No scenes yet'}
                </p>
              ) : (
                (storybook.scenes || []).map((scene, index) => (
                  <div
                    key={`scene-${index}-${scene.imageUrl || scene.name}`}
                    className="character-card"
                    draggable={!!scene.imageUrl}
                    onDragStart={(e) => {
                      if (scene.imageUrl) {
                        handleDragStart(e, scene.imageUrl, scene.name, scene.prompt);
                        handleDeleteDragStart(e, 'scene', scene.name, scene.imageUrl);
                      }
                    }}
                    onDragEnd={handleDeleteDragEnd}
                    onClick={() => scene.imageUrl && onAddImage(scene.imageUrl, undefined, undefined, scene.prompt, scene.name)}
                    style={{ cursor: scene.imageUrl ? 'grab' : 'default' }}
                    title={scene.prompt ? `${scene.name}\n\nComposition Prompt: ${scene.prompt}` : scene.name}
                  >
                    {scene.imageUrl ? (
                      <>
                        <img src={getImageUrl(getThumbnailOrFullUrl(scene.imageUrl, scene.thumbnailUrl, true, 'scene', scene.name)!)} alt={scene.name} draggable={false} />
                        {hasApiAccess && onEditImage && (
                          <button
                            className="edit-image-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(scene.imageUrl!, scene.name, 'scene', scene.prompt);
                            }}
                            title="Edit image with AI"
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          className="download-image-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadImage(scene.imageUrl!, scene.name);
                          }}
                          title="Download image"
                        >
                          ⬇️
                        </button>
                      </>
                    ) : (
                      <div className="character-placeholder">Generating...</div>
                    )}
                    <p className="character-name" title={scene.prompt ? `Composition Prompt: ${scene.prompt}` : scene.name}>{scene.name}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="sidebar-info">
          <div className="info-item">
            {new Date(storybook.createdAt).toLocaleDateString()}
          </div>
        </div>

        {/* Delete Zone */}
          <div
            className={`delete-zone ${isDeleteZoneActive ? 'active' : ''}`}
            onDrop={handleDeleteDrop}
            onDragOver={handleDeleteDragOver}
          >
            <span className="delete-icon" aria-hidden="true">×</span>
            <span className="delete-text">Drop here to delete</span>
          </div>
        </section>
      </div>

      {showAssetModal && (
        <div className="modal-overlay" onClick={() => setShowAssetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Generate New Asset</h2>
            <form onSubmit={handleGenerateAsset}>
              <div className="form-group">
                <label className="form-label">AI Model</label>
                <select
                  className="form-input"
                  value={assetModel}
                  onChange={(e) => setAssetModel(e.target.value as 'chatgpt' | 'gemini')}
                >
                  <option value="gemini">Nano Banana (20 tokens, Fast)</option>
                  <option value="chatgpt">ChatGPT (40 tokens, Slow)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Asset Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  required
                  autoFocus
                  placeholder="e.g., HAHA Text Effect"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={assetPrompt}
                  onChange={(e) => setAssetPrompt(e.target.value)}
                  required
                  rows={4}
                  placeholder="e.g., Text saying 'HAHA!' with big colorful letters and comic book style explosion effect"
                />
                <p className="help-text">
                  💡 The asset will be generated with a transparent background, no props or shadows, and clean outlines matching your book's art style.
                </p>
              </div>

              {/* Reference Images Selection */}
              {(storybook.assets?.length > 0 || storybook.characters?.length > 0) && (
                <div className="form-group">
                  <label className="form-label">Reference Images (Optional)</label>
                  <p className="help-text" style={{ marginBottom: '8px' }}>
                    Select existing images to use as style reference
                  </p>
                  <div className="reference-grid">
                    {storybook.assets?.filter(asset => asset.imageUrl).map((asset, index) => (
                      <div 
                        key={`asset-ref-${asset.name}-${asset.imageUrl}-${index}`}
                        className={`reference-item ${assetRefImages.includes(asset.imageUrl!) ? 'selected' : ''}`}
                        onClick={() => toggleAssetRefSelection(asset.imageUrl!)}
                      >
                        <img src={getImageUrl(getThumbnailOrFullUrl(asset.imageUrl!, asset.thumbnailUrl, true, 'asset', asset.name)!)} alt={asset.name} />
                        <span className="reference-name">{asset.name}</span>
                        {assetRefImages.includes(asset.imageUrl!) && <div className="selection-badge">✓</div>}
                      </div>
                    ))}
                    {storybook.characters?.filter(char => char.imageUrl).map((char, index) => (
                      <div 
                        key={`asset-char-${char.name}-${char.imageUrl}-${index}`}
                        className={`reference-item ${assetRefImages.includes(char.imageUrl!) ? 'selected' : ''}`}
                        onClick={() => toggleAssetRefSelection(char.imageUrl!)}
                      >
                        <img src={getImageUrl(getThumbnailOrFullUrl(char.imageUrl!, char.thumbnailUrl, true, 'character', char.name)!)} alt={char.name} />
                        <span className="reference-name">{char.name}</span>
                        {assetRefImages.includes(char.imageUrl!) && <div className="selection-badge">✓</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Background Style</label>
                <select
                  className="form-input"
                  value={assetBackgroundOption}
                  onChange={(e) => setAssetBackgroundOption(e.target.value as 'transparent' | 'full-color')}
                >
                  <option value="transparent">Transparent Background (recommended)</option>
                  <option value="full-color">Full Color Background</option>
                </select>
                {assetBackgroundOption === 'transparent' && (
                  <div className="form-group" style={{ marginTop: '10px' }}>
                    <label className="form-label">Background Color</label>
                    <select
                      className="form-input"
                      value={assetChromaKeyColor}
                      onChange={(e) => setAssetChromaKeyColor(e.target.value as 'white' | 'green' | 'blue')}
                    >
                      <option value="green">Green (recommended for assets)</option>
                      <option value="white">White</option>
                      <option value="blue">Blue</option>
                    </select>
                    <p className="help-text">
                      AI will generate with this background color, then automatically detect and remove it
                    </p>
                  </div>
                )}
                <p className="help-text">
                  {assetBackgroundOption === 'transparent' 
                    ? 'Asset will have transparent background for easy placement' 
                    : 'Asset will include environment/background'}
                </p>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowAssetModal(false)}
                  disabled={showGenerationProgress}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={showGenerationProgress}
                >
                  {showGenerationProgress ? 'Generating...' : 'Generate Asset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCharacterModal && (
        <div className="modal-overlay" onClick={() => setShowCharacterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Generate New Character</h2>
            <form onSubmit={handleGenerateCharacter}>
              <div className="form-group">
                <label className="form-label">AI Model</label>
                <select
                  className="form-input"
                  value={characterModel}
                  onChange={(e) => setCharacterModel(e.target.value as 'chatgpt' | 'gemini')}
                >
                  <option value="gemini">Nano Banana (20 tokens, Fast)</option>
                  <option value="chatgpt">ChatGPT (40 tokens, Slow)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Character Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={characterName}
                  onChange={(e) => setCharacterName(e.target.value)}
                  required
                  autoFocus
                  placeholder="e.g., Little Fox"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={characterPrompt}
                  onChange={(e) => setCharacterPrompt(e.target.value)}
                  required
                  rows={4}
                  placeholder="e.g., A cute orange fox with big eyes, wearing a blue scarf"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Character Size</label>
                <select
                  className="form-input"
                  value={characterHeightCategory}
                  onChange={(e) => setCharacterHeightCategory(e.target.value as any)}
                >
                  <option value="tiny">Tiny (baby/toddler/tiny animal)</option>
                  <option value="small">Small (young child/small character)</option>
                  <option value="medium">Medium (older child/teen)</option>
                  <option value="tall">Tall (average adult)</option>
                  <option value="very-tall">Very Tall (tall adult/large character)</option>
                </select>
                <p className="help-text">
                  Used during scene composition to maintain correct size relationships between characters
                </p>
              </div>

              {/* Reference Images Selection */}
              {(storybook.characters?.length > 0 || storybook.assets?.length > 0) && (
                <div className="form-group">
                  <label className="form-label">Reference Images (Optional)</label>
                  <p className="help-text" style={{ marginBottom: '8px' }}>
                    Select existing characters or assets to use as style reference
                  </p>
                  
                  {/* Reference Characters */}
                  {storybook.characters?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#666' }}>
                        Characters
                      </div>
                      <div className="reference-grid">
                        {storybook.characters.filter(char => char.imageUrl).map((char, index) => (
                          <div 
                            key={`char-ref-${char.name}-${char.imageUrl}-${index}`}
                            className={`reference-item ${characterRefImages.includes(char.imageUrl!) ? 'selected' : ''}`}
                            onClick={() => toggleCharacterRefSelection(char.imageUrl!)}
                          >
                            <img src={getImageUrl(getThumbnailOrFullUrl(char.imageUrl!, char.thumbnailUrl, true, 'character', char.name)!)} alt={char.name} />
                            <span className="reference-name">{char.name}</span>
                            {characterRefImages.includes(char.imageUrl!) && <div className="selection-badge">✓</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reference Assets */}
                  {storybook.assets?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#666' }}>
                        Assets
                      </div>
                      <div className="reference-grid">
                        {storybook.assets.filter(asset => asset.imageUrl).map((asset, index) => (
                          <div 
                            key={`char-asset-${asset.name}-${asset.imageUrl}-${index}`}
                            className={`reference-item ${characterRefImages.includes(asset.imageUrl!) ? 'selected' : ''}`}
                            onClick={() => toggleCharacterRefSelection(asset.imageUrl!)}
                          >
                            <img src={getImageUrl(getThumbnailOrFullUrl(asset.imageUrl!, asset.thumbnailUrl, true, 'asset', asset.name)!)} alt={asset.name} />
                            <span className="reference-name">{asset.name}</span>
                            {characterRefImages.includes(asset.imageUrl!) && <div className="selection-badge">✓</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Background Style</label>
                <select
                  className="form-input"
                  value={characterBackgroundOption}
                  onChange={(e) => setCharacterBackgroundOption(e.target.value as 'transparent' | 'full-color')}
                >
                  <option value="full-color">Full Color Background (recommended)</option>
                  <option value="transparent">Transparent Background</option>
                </select>
                {characterBackgroundOption === 'transparent' && (
                  <div className="form-group" style={{ marginTop: '10px' }}>
                    <label className="form-label">Background Color</label>
                    <select
                      className="form-input"
                      value={characterChromaKeyColor}
                      onChange={(e) => setCharacterChromaKeyColor(e.target.value as 'white' | 'green' | 'blue')}
                    >
                      <option value="white">White (recommended for characters)</option>
                      <option value="green">Green</option>
                      <option value="blue">Blue</option>
                    </select>
                    <p className="help-text">
                      AI will generate with this background color, then automatically detect and remove it
                    </p>
                  </div>
                )}
                <p className="help-text">
                  {characterBackgroundOption === 'transparent' 
                    ? 'Character will have transparent background for easy placement' 
                    : 'Character will include environment/background'}
                </p>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowCharacterModal(false)}
                  disabled={showGenerationProgress}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={showGenerationProgress}
                >
                  {showGenerationProgress ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBackgroundModal && (
        <div className="modal-overlay" onClick={() => setShowBackgroundModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Generate New Background</h2>
            <form onSubmit={handleGenerateBackground}>
              <div className="form-group">
                <label className="form-label">AI Model</label>
                <select
                  className="form-input"
                  value={backgroundModel}
                  onChange={(e) => setBackgroundModel(e.target.value as 'chatgpt' | 'gemini')}
                >
                  <option value="gemini">Nano Banana (20 tokens, Fast)</option>
                  <option value="chatgpt">ChatGPT (40 tokens, Slow)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Background Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={backgroundName}
                  onChange={(e) => setBackgroundName(e.target.value)}
                  required
                  autoFocus
                  placeholder="e.g., Enchanted Forest"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={backgroundPrompt}
                  onChange={(e) => setBackgroundPrompt(e.target.value)}
                  required
                  rows={4}
                  placeholder="e.g., A magical forest with tall trees, glowing mushrooms, and a winding path"
                />
              </div>

              {/* Reference Images Selection */}
              {storybook.backgrounds?.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Reference Backgrounds (Optional)</label>
                  <p className="help-text" style={{ marginBottom: '8px' }}>
                    Select existing backgrounds to use as style reference
                  </p>
                  <div className="reference-grid">
                    {storybook.backgrounds.filter(bg => bg.imageUrl).map((bg) => (
                      <div 
                        key={bg.name}
                        className={`reference-item ${backgroundRefImages.includes(bg.imageUrl!) ? 'selected' : ''}`}
                        onClick={() => toggleBackgroundRefSelection(bg.imageUrl!)}
                      >
                        <img src={getImageUrl(getThumbnailOrFullUrl(bg.imageUrl!, bg.thumbnailUrl, true, 'background', bg.name)!)} alt={bg.name} />
                        <span className="reference-name">{bg.name}</span>
                        {backgroundRefImages.includes(bg.imageUrl!) && <div className="selection-badge">✓</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* View Transformation - TEMPORARILY DISABLED due to Gemini model issues */}
              {false && backgroundRefImages.length > 0 && (
                <div className="form-group">
                  <label className="form-label">View Transformations (Optional - Select from each category)</label>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                    {/* Rotation */}
                    <div>
                      <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Rotation</label>
                      <select
                        className="form-input"
                        value={bgRotation}
                        onChange={(e) => setBgRotation(e.target.value)}
                        style={{ fontSize: '13px' }}
                      >
                        <option value="">None</option>
                        <option value="rotate-left-45">↺ 45° Left</option>
                        <option value="rotate-left-90">↺ 90° Left</option>
                        <option value="rotate-right-45">↻ 45° Right</option>
                        <option value="rotate-right-90">↻ 90° Right</option>
                        <option value="rotate-180">⟲ 180° (Reverse)</option>
                      </select>
                    </div>

                    {/* Vertical Angle */}
                    <div>
                      <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Vertical Angle</label>
                      <select
                        className="form-input"
                        value={bgVerticalAngle}
                        onChange={(e) => setBgVerticalAngle(e.target.value)}
                        style={{ fontSize: '13px' }}
                      >
                        <option value="">None</option>
                        <option value="look-up">⬆ Look Up</option>
                        <option value="look-down">⬇ Look Down</option>
                      </select>
                    </div>

                    {/* Zoom */}
                    <div>
                      <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Zoom</label>
                      <select
                        className="form-input"
                        value={bgZoom}
                        onChange={(e) => setBgZoom(e.target.value)}
                        style={{ fontSize: '13px' }}
                      >
                        <option value="">None</option>
                        <option value="zoom-in">🔍 Zoom In</option>
                        <option value="zoom-out">🔎 Zoom Out</option>
                      </select>
                    </div>

                    {/* Pan */}
                    <div>
                      <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Pan</label>
                      <select
                        className="form-input"
                        value={bgPan}
                        onChange={(e) => setBgPan(e.target.value)}
                        style={{ fontSize: '13px' }}
                      >
                        <option value="">None</option>
                        <option value="pan-left">⬅ Pan Left</option>
                        <option value="pan-right">➡ Pan Right</option>
                      </select>
                    </div>
                  </div>

                  {/* Special Views - Full width */}
                  <div>
                    <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Special Views</label>
                    <select
                      className="form-input"
                      value={bgSpecialView}
                      onChange={(e) => setBgSpecialView(e.target.value)}
                      style={{ fontSize: '13px' }}
                    >
                      <option value="">None</option>
                      <option value="below-surface">⬇ Below Surface (Underground/Underwater)</option>
                      <option value="above-surface">⬆ Above Surface (Aerial)</option>
                      <option value="inside-view">🚪 Inside View (Interior)</option>
                      <option value="behind-object">🔄 Behind Object</option>
                    </select>
                  </div>

                  <p className="help-text" style={{ marginTop: '8px' }}>
                    {[bgRotation, bgVerticalAngle, bgZoom, bgPan, bgSpecialView].filter(v => v).length > 0 
                      ? `${[bgRotation, bgVerticalAngle, bgZoom, bgPan, bgSpecialView].filter(v => v).length} transformation(s) selected - they will be combined`
                      : 'Select transformations to apply to the reference view'}
                  </p>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowBackgroundModal(false)}
                  disabled={showGenerationProgress}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={showGenerationProgress}
                >
                  {showGenerationProgress ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSceneModal && (
        <div className="modal-overlay" onClick={() => setShowSceneModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>AI Scene Composer</h2>
            <p className="help-text" style={{ marginTop: '0', marginBottom: '16px', fontSize: '14px', color: '#666' }}>
              ✨ Powered by Nano Banana (Gemini) - 20 tokens per generation
            </p>
            <form onSubmit={handleGenerateScene}>
              <div className="form-group">
                <label className="form-label">Scene Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={sceneName}
                  onChange={(e) => setSceneName(e.target.value)}
                  required
                  autoFocus
                  placeholder="e.g., Forest Adventure"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Composition Prompt</label>
                <textarea
                  className="form-input"
                  value={scenePrompt}
                  onChange={(e) => setScenePrompt(e.target.value)}
                  required
                  rows={2}
                  placeholder="e.g., Fox walking through the forest path at sunset"
                />
                <p className="help-text">Describe how to combine the selected assets</p>
              </div>

              <div className="form-group">
                <label className="form-label">Background Style</label>
                <select
                  className="form-input"
                  value={sceneBackgroundOption}
                  onChange={(e) => setSceneBackgroundOption(e.target.value as 'transparent' | 'full-color')}
                >
                  <option value="full-color">Full Color Background</option>
                  <option value="transparent">Transparent Background</option>
                </select>
                {sceneBackgroundOption === 'transparent' && (
                  <div className="form-group" style={{ marginTop: '10px' }}>
                    <label className="form-label">Background Color</label>
                    <select
                      className="form-input"
                      value={chromaKeyColor}
                      onChange={(e) => setChromaKeyColor(e.target.value as 'white' | 'green' | 'blue')}
                    >
                      <option value="green">Green (recommended)</option>
                      <option value="blue">Blue</option>
                      <option value="white">White</option>
                    </select>
                    <p className="help-text">
                      AI will generate with this background color, then automatically detect and remove it
                    </p>
                  </div>
                )}
                <p className="help-text">
                  {sceneBackgroundOption === 'transparent' 
                    ? 'Chroma key areas will be made fully transparent' 
                    : 'Scene will have a full background'}
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Camera View / Angle</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <select
                    className="form-input"
                    style={{ flex: 1 }}
                    onChange={(e) => setCameraView(e.target.value)}
                    value={cameraView}
                  >
                    <option value="">-- Select a camera angle or type custom --</option>
                    <option value="eye level view, neutral perspective">Eye Level (Neutral)</option>
                    <option value="high angle view, looking down at characters">High Angle (Bird's Eye)</option>
                    <option value="low angle view, looking up at characters">Low Angle (Worm's Eye)</option>
                    <option value="over-the-shoulder view, from behind one character">Over the Shoulder</option>
                    <option value="close-up view, focusing on character faces and expressions">Close-Up (Face/Expression)</option>
                    <option value="medium shot, showing characters from waist up">Medium Shot (Waist Up)</option>
                    <option value="full shot, showing characters from head to toe">Full Shot (Head to Toe)</option>
                    <option value="wide shot, showing entire scene with environment">Wide Shot (Establishing)</option>
                    <option value="extreme close-up, focusing on specific detail">Extreme Close-Up (Detail)</option>
                    <option value="dutch angle, tilted diagonal perspective">Dutch Angle (Tilted)</option>
                    <option value="aerial view, directly from above">Aerial View (Top-Down)</option>
                    <option value="three-quarter view, angled perspective">Three-Quarter View</option>
                    <option value="side profile view, characters in silhouette">Side Profile</option>
                    <option value="first-person POV, seeing through character's eyes">First-Person POV</option>
                  </select>
                </div>
                <input
                  type="text"
                  className="form-input"
                  value={cameraView}
                  onChange={(e) => setCameraView(e.target.value)}
                  placeholder="Or type custom camera angle (e.g., 'dramatic low angle with shadows')"
                />
                <p className="help-text">Choose from common angles or describe your own camera perspective</p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Select Background {selectedBackground ? '(1 selected)' : '(Optional)'}
                </label>
                <div className="character-selection-grid">
                  {(storybook.backgrounds || []).map((bg, index) => (
                    <div
                      key={`scene-bg-${index}-${bg.imageUrl || bg.name}`}
                      className={`character-select-card ${selectedBackground === bg.name ? 'selected' : ''}`}
                      onClick={() => setSelectedBackground(selectedBackground === bg.name ? '' : bg.name)}
                    >
                      {bg.imageUrl ? (
                        <img src={getImageUrl(bg.imageUrl)} alt={bg.name} />
                      ) : (
                        <div className="character-placeholder">...</div>
                      )}
                      <p className="character-name" title={bg.name}>
                        {bg.name.length > 20 ? bg.name.substring(0, 20) + '...' : bg.name}
                      </p>
                      {selectedBackground === bg.name && (
                        <div className="selected-badge">✓</div>
                      )}
                    </div>
                  ))}
                </div>
                {(storybook.backgrounds || []).length === 0 && (
                  <p className="help-text">No backgrounds available. Generate backgrounds first.</p>
                )}
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  Select Characters ({selectedCharacters.length} selected)
                </label>
                <div className="character-selection-grid">
                  {storybook.characters.map((char, index) => (
                    <div
                      key={`scene-char-${index}-${char.imageUrl || char.name}`}
                      className={`character-select-card ${selectedCharacters.includes(char.name) ? 'selected' : ''}`}
                      onClick={() => toggleCharacterSelection(char.name)}
                    >
                      {char.imageUrl ? (
                        <img src={getImageUrl(char.imageUrl)} alt={char.name} />
                      ) : (
                        <div className="character-placeholder">...</div>
                      )}
                      <p className="character-name" title={char.name}>
                        {char.name.length > 20 ? char.name.substring(0, 20) + '...' : char.name}
                      </p>
                      {selectedCharacters.includes(char.name) && (
                        <div className="selected-badge">✓</div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="help-text">Click to add/remove characters</p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Select Assets ({selectedAssets.length} selected) - Optional
                </label>
                <div className="character-selection-grid">
                  {(storybook.assets || []).map((asset, index) => (
                    <div
                      key={`scene-asset-${index}-${asset.imageUrl || asset.name}`}
                      className={`character-select-card ${selectedAssets.includes(asset.name) ? 'selected' : ''}`}
                      onClick={() => toggleAssetSelection(asset.name)}
                    >
                      {asset.imageUrl ? (
                        <img src={getImageUrl(asset.imageUrl)} alt={asset.name} />
                      ) : (
                        <div className="character-placeholder">...</div>
                      )}
                      <p className="character-name" title={asset.name}>
                        {asset.name.length > 20 ? asset.name.substring(0, 20) + '...' : asset.name}
                      </p>
                      {selectedAssets.includes(asset.name) && (
                        <div className="selected-badge">✓</div>
                      )}
                    </div>
                  ))}
                </div>
                {(storybook.assets || []).length === 0 ? (
                  <p className="help-text">No assets available. Generate assets first.</p>
                ) : (
                  <p className="help-text">Click to add/remove assets (decorative elements, text effects, etc.)</p>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowSceneModal(false);
                    setSelectedCharacters([]);
                    setSelectedAssets([]);
                    setSelectedBackground('');
                    setScenePrompt('');
                    setCameraView('');
                  }}
                  disabled={showGenerationProgress}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={showGenerationProgress}
                >
                  {showGenerationProgress ? 'Composing...' : 'Compose Scene'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Image Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-edit-split" onClick={(e) => e.stopPropagation()}>
            <h2>Edit {editImageType === 'asset' ? 'Asset' : editImageType === 'character' ? 'Character' : editImageType === 'background' ? 'Background' : 'Scene'}</h2>
            
            <div className="edit-modal-container">
              {/* Left Side - Image with Lasso Selection */}
              <div className="edit-modal-left">
                <div className="image-preview-section">
                  <div className="image-canvas-wrapper">
                    <img 
                      ref={(img) => {
                        setImageElement(img);
                        if (img && canvasRef.current) {
                          const canvas = canvasRef.current;
                          canvas.width = img.naturalWidth;
                          canvas.height = img.naturalHeight;
                          
                          // Position canvas to exactly overlay the image
                          const updateCanvasPosition = () => {
                            if (img && canvasRef.current) {
                              const imgRect = img.getBoundingClientRect();
                              const wrapperRect = img.parentElement?.getBoundingClientRect();
                              if (wrapperRect) {
                                const canvas = canvasRef.current;
                                canvas.style.left = `${imgRect.left - wrapperRect.left}px`;
                                canvas.style.top = `${imgRect.top - wrapperRect.top}px`;
                                canvas.style.width = `${imgRect.width}px`;
                                canvas.style.height = `${imgRect.height}px`;
                              }
                            }
                          };
                          
                          // Update position after image loads
                          img.onload = updateCanvasPosition;
                          // Also update immediately in case image is cached
                          setTimeout(updateCanvasPosition, 0);
                          
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            ctx.clearRect(0, 0, canvas.width, canvas.height);
                          }
                        }
                      }}
                      src={getImageUrl(editImageUrl)} 
                      alt={editImageName}
                      className="edit-preview-image"
                    />
                    <canvas
                      ref={canvasRef}
                      className="lasso-canvas active"
                      onMouseDown={(e) => {
                        if (!canvasRef.current || !imageElement) return;
                        e.preventDefault();
                        
                        // Enable lasso mode on first draw
                        if (lassoMode === 'none') {
                          setLassoMode('inside');
                        }
                        
                        // ALWAYS ensure canvas is properly positioned before drawing
                        const canvas = canvasRef.current;
                        const imgRect = imageElement.getBoundingClientRect();
                        const wrapperRect = imageElement.parentElement?.getBoundingClientRect();
                        
                        if (!wrapperRect || imgRect.width === 0 || imgRect.height === 0) {
                          console.warn('Image not ready for lasso drawing');
                          return;
                        }
                        
                        // Update canvas position
                        canvas.style.left = `${imgRect.left - wrapperRect.left}px`;
                        canvas.style.top = `${imgRect.top - wrapperRect.top}px`;
                        canvas.style.width = `${imgRect.width}px`;
                        canvas.style.height = `${imgRect.height}px`;
                        
                        // Ensure canvas internal dimensions match image natural size
                        if (canvas.width !== imageElement.naturalWidth || canvas.height !== imageElement.naturalHeight) {
                          canvas.width = imageElement.naturalWidth;
                          canvas.height = imageElement.naturalHeight;
                        }
                        
                        // Get canvas bounding rect AFTER positioning
                        const canvasRect = canvas.getBoundingClientRect();
                        
                        // Calculate mouse position relative to the canvas
                        const mouseX = e.clientX - canvasRect.left;
                        const mouseY = e.clientY - canvasRect.top;
                        
                        // Convert to canvas internal coordinates (accounting for canvas width/height vs display size)
                        const x = (mouseX / canvasRect.width) * canvas.width;
                        const y = (mouseY / canvasRect.height) * canvas.height;
                        
                        console.log('First click:', { mouseX, mouseY, canvasX: x, canvasY: y, canvasRect, canvasWidth: canvas.width, canvasHeight: canvas.height });
                        
                        const point = {x, y};
                        lassoPointsRef.current = [point];
                        setIsDrawingLasso(true);
                        setLassoPoints([point]);
                      }}
                      onMouseMove={(e) => {
                        if (!isDrawingLasso || !canvasRef.current || !imageElement) return;
                        e.preventDefault();
                        
                        // Get canvas bounding rect for accurate coordinate calculation
                        const canvas = canvasRef.current;
                        const canvasRect = canvas.getBoundingClientRect();
                        
                        // Calculate mouse position relative to the canvas
                        const mouseX = e.clientX - canvasRect.left;
                        const mouseY = e.clientY - canvasRect.top;
                        
                        // Convert to canvas internal coordinates
                        const x = (mouseX / canvasRect.width) * canvas.width;
                        const y = (mouseY / canvasRect.height) * canvas.height;
                        
                        // Check if close to starting point for auto-close
                        // Require at least 20 points to prevent accidental immediate closure
                        if (lassoPointsRef.current.length > 20) {
                          const startPoint = lassoPointsRef.current[0];
                          const distance = Math.sqrt(Math.pow(x - startPoint.x, 2) + Math.pow(y - startPoint.y, 2));
                          const threshold = 15; // pixels
                          
                          if (distance < threshold) {
                            // Auto-close the lasso with animation
                            const ctx = canvasRef.current.getContext('2d');
                            if (ctx) {
                              // Cancel any existing animation
                              if (animationFrameRef.current) {
                                cancelAnimationFrame(animationFrameRef.current);
                              }
                              
                              // Animate the selection
                              let dashOffset = 0;
                              const animateSelection = () => {
                                if (!canvasRef.current || lassoPointsRef.current.length === 0) return;
                                
                                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                
                                // Draw the closed path with marching ants
                                ctx.setLineDash([8, 4]);
                                ctx.lineDashOffset = dashOffset;
                                ctx.strokeStyle = '#3B82F6';
                                ctx.lineWidth = 3;
                                ctx.lineCap = 'round';
                                ctx.lineJoin = 'round';
                                ctx.beginPath();
                                ctx.moveTo(lassoPointsRef.current[0].x, lassoPointsRef.current[0].y);
                                for (let i = 1; i < lassoPointsRef.current.length; i++) {
                                  ctx.lineTo(lassoPointsRef.current[i].x, lassoPointsRef.current[i].y);
                                }
                                ctx.closePath();
                                ctx.stroke();
                                ctx.setLineDash([]);
                                
                                // Pulsing fill for selection area (no gray overlay, just highlight)
                                const pulseAlpha = 0.15 + Math.sin(dashOffset / 5) * 0.05;
                                ctx.fillStyle = lassoMode === 'inside' 
                                  ? `rgba(59, 130, 246, ${pulseAlpha})` 
                                  : `rgba(239, 68, 68, ${pulseAlpha})`;
                                ctx.fill();
                                
                                dashOffset -= 0.5;
                                
                                animationFrameRef.current = requestAnimationFrame(animateSelection);
                              };
                              animateSelection();
                            }
                            setLassoPoints([...lassoPointsRef.current]);
                            setIsDrawingLasso(false);
                            return;
                          }
                        }
                        
                        // Add new point to ref
                        lassoPointsRef.current.push({x, y});
                        
                        // Use requestAnimationFrame for smooth drawing
                        if (!drawRequestRef.current) {
                          drawRequestRef.current = requestAnimationFrame(() => {
                            drawRequestRef.current = null;
                            const ctx = canvasRef.current?.getContext('2d');
                            if (ctx && canvasRef.current) {
                              ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                              ctx.strokeStyle = '#3B82F6';
                              ctx.lineWidth = 3;
                              ctx.lineCap = 'round';
                              ctx.lineJoin = 'round';
                              ctx.beginPath();
                              ctx.moveTo(lassoPointsRef.current[0].x, lassoPointsRef.current[0].y);
                              for (let i = 1; i < lassoPointsRef.current.length; i++) {
                                ctx.lineTo(lassoPointsRef.current[i].x, lassoPointsRef.current[i].y);
                              }
                              ctx.stroke();
                              
                              // Draw a circle at the starting point if we have enough points
                              if (lassoPointsRef.current.length > 3) {
                                ctx.beginPath();
                                ctx.arc(lassoPointsRef.current[0].x, lassoPointsRef.current[0].y, 8, 0, 2 * Math.PI);
                                ctx.fillStyle = '#3B82F6';
                                ctx.fill();
                                ctx.strokeStyle = 'white';
                                ctx.lineWidth = 2;
                                ctx.stroke();
                              }
                            }
                          });
                        }
                      }}
                      onMouseUp={(e) => {
                        if (isDrawingLasso && lassoPointsRef.current.length > 2) {
                          e.preventDefault();
                          // Close the loop with animation
                          const ctx = canvasRef.current?.getContext('2d');
                          if (ctx && canvasRef.current) {
                            // Cancel any existing animation
                            if (animationFrameRef.current) {
                              cancelAnimationFrame(animationFrameRef.current);
                            }
                            
                            // Animate the selection
                            let dashOffset = 0;
                            const animateSelection = () => {
                              if (!canvasRef.current || lassoPointsRef.current.length === 0) return;
                              
                              ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                              
                              // Draw the closed path with marching ants
                              ctx.setLineDash([8, 4]);
                              ctx.lineDashOffset = dashOffset;
                              ctx.strokeStyle = '#3B82F6';
                              ctx.lineWidth = 3;
                              ctx.lineCap = 'round';
                              ctx.lineJoin = 'round';
                              ctx.beginPath();
                              ctx.moveTo(lassoPointsRef.current[0].x, lassoPointsRef.current[0].y);
                              for (let i = 1; i < lassoPointsRef.current.length; i++) {
                                ctx.lineTo(lassoPointsRef.current[i].x, lassoPointsRef.current[i].y);
                              }
                              ctx.closePath();
                              ctx.stroke();
                              ctx.setLineDash([]);
                              
                              // Pulsing fill for selection area (no gray overlay, just highlight)
                              const pulseAlpha = 0.15 + Math.sin(dashOffset / 5) * 0.05;
                              ctx.fillStyle = lassoMode === 'inside' 
                                ? `rgba(59, 130, 246, ${pulseAlpha})` 
                                : `rgba(239, 68, 68, ${pulseAlpha})`;
                              ctx.fill();
                              
                              dashOffset -= 0.5;
                              animationFrameRef.current = requestAnimationFrame(animateSelection);
                            };
                            animateSelection();
                          }
                          // Update state to match ref
                          setLassoPoints([...lassoPointsRef.current]);
                        }
                        setIsDrawingLasso(false);
                      }}
                      onMouseLeave={() => {
                        setIsDrawingLasso(false);
                      }}
                    />
                  </div>
                  
                  <div className="lasso-controls">
                    <p className="help-text" style={{ margin: '8px 0', fontSize: '14px', color: '#6b7280' }}>
                      💡 <strong>Tip:</strong> Draw a lasso around any area to edit only that region
                    </p>
                    {lassoPoints.length > 0 && (
                      <button
                        type="button"
                        className="btn-lasso btn-clear"
                        onClick={() => {
                          if (animationFrameRef.current) {
                            cancelAnimationFrame(animationFrameRef.current);
                            animationFrameRef.current = null;
                          }
                          setLassoPoints([]);
                          lassoPointsRef.current = [];
                          setLassoMode('none');
                          const ctx = canvasRef.current?.getContext('2d');
                          if (ctx && canvasRef.current) {
                            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                          }
                        }}
                      >
                        🗑️ Clear Selection
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side - Options */}
              <div className="edit-modal-right">
                <form onSubmit={handleEditImage}>
                  {editOriginalPrompt && (
                    <div className="form-group">
                      <label className="form-label">Original Prompt</label>
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#374151',
                        border: '1px solid #e5e7eb'
                      }}>
                        {editOriginalPrompt}
                      </div>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label className="form-label">New Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editNewName}
                      onChange={(e) => setEditNewName(e.target.value)}
                      required
                      placeholder="e.g., Character Name - edited"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Edit Instructions (Optional)</label>
                    <textarea
                      className="form-input"
                      value={editPrompt}
                      onChange={(e) => setEditPrompt(e.target.value)}
                      rows={4}
                      autoFocus
                      placeholder={lassoPoints.length > 0 
                        ? (lassoMode === 'inside' 
                          ? "e.g., Remove it, Change to flowers, Make it blue" 
                          : "e.g., Remove everything except the selected area, Blur the background")
                        : "e.g., Remove her backpack, Add flowers, Change background to blue"
                      }
                    />
                    
                    {lassoPoints.length > 0 ? (
                      <div className="prompt-examples">
                        <p className="help-text" style={{ fontWeight: '600', marginBottom: '8px' }}>
                          💡 Quick Actions - edits only inside the yellow box:
                        </p>
                        <div className="example-prompts">
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Remove the object')}>
                            🗑️ Remove the object
                          </button>
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Add text ""')}>
                            ✏️ Add text ""
                          </button>
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Change to a red apple')}>
                            🔄 Change to...
                          </button>
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Make it glow with magic')}>
                            ✨ Add effects
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="prompt-examples">
                        <p className="help-text" style={{ fontWeight: '600', marginBottom: '8px' }}>
                          💡 Quick Actions - edits the entire image:
                        </p>
                        <div className="example-prompts">
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Remove the hat')}>
                            🗑️ Remove an object
                          </button>
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Add a red scarf')}>
                            ➕ Add an object
                          </button>
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Change background to forest')}>
                            🌲 Change background
                          </button>
                          <button type="button" className="example-prompt" onClick={() => setEditPrompt('Make brighter')}>
                            ☀️ Adjust lighting
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Background Style Options - Only show for character, asset, and scene */}
                  {(editImageType === 'character' || editImageType === 'asset' || editImageType === 'scene') && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Background Style</label>
                        <select
                          className="form-input"
                          value={editBackgroundOption}
                          onChange={(e) => setEditBackgroundOption(e.target.value as 'transparent' | 'full-color')}
                        >
                          <option value="transparent">Transparent Background</option>
                          <option value="full-color">Full Color Background</option>
                        </select>
                      </div>

                      {/* Chroma Key Color - Only show when transparent background is selected */}
                      {editBackgroundOption === 'transparent' && (
                        <div className="form-group">
                          <label className="form-label">Chroma Key Color</label>
                          <select
                            className="form-input"
                            value={editChromaKeyColor}
                            onChange={(e) => setEditChromaKeyColor(e.target.value as 'white' | 'green' | 'blue')}
                          >
                            <option value="white">White Screen</option>
                            <option value="green">Green Screen</option>
                            <option value="blue">Blue Screen</option>
                          </select>
                          <p className="help-text">
                            Select the color to make transparent
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Aspect Ratio */}
                  <div className="form-group">
                    <label className="form-label">
                      Aspect Ratio
                    </label>
                    <select
                      className="form-input"
                      value={editAspectRatio}
                      onChange={(e) => setEditAspectRatio(e.target.value)}
                    >
                      <option value="1:1">1:1 (Square)</option>
                      <option value="16:9">16:9 (Landscape)</option>
                      <option value="9:16">9:16 (Portrait)</option>
                      <option value="4:3">4:3 (Standard)</option>
                      <option value="3:4">3:4 (Vertical Standard)</option>
                      <option value="21:9">21:9 (Ultra Wide)</option>
                      <option value="9:21">9:21 (Ultra Tall)</option>
                    </select>
                  </div>

                  {/* Camera Angle */}
                  <div className="form-group">
                    <label className="form-label">
                      Camera Angle
                    </label>
                    <select
                      className="form-input"
                      value={editCameraAngle}
                      onChange={(e) => setEditCameraAngle(e.target.value)}
                    >
                      <option value="eye-level">Eye Level (Normal View)</option>
                      <option value="low-angle">Low Angle (Looking Up)</option>
                      <option value="high-angle">High Angle (Looking Down)</option>
                      <option value="birds-eye">Bird's Eye (Top Down)</option>
                      <option value="worms-eye">Worm's Eye (Ground Up)</option>
                      <option value="dutch-angle">Dutch Angle (Tilted)</option>
                      <option value="over-shoulder">Over the Shoulder</option>
                      <option value="close-up">Close-Up</option>
                      <option value="wide-shot">Wide Shot</option>
                      <option value="medium-shot">Medium Shot</option>
                    </select>
                  </div>

                  {/* Reference Images from Gallery */}
                  <div className="form-group">
                    <label className="form-label">
                      Reference Images (Optional)
                    </label>
                    <div className="character-selection-grid" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {editImageType === 'asset' && (storybook.assets || []).map((asset, index) => {
                        if (!asset.imageUrl) return null;
                        const fullImgUrl = getImageUrl(asset.imageUrl);
                        const thumbnailUrl = getImageUrl(getThumbnailOrFullUrl(asset.imageUrl, asset.thumbnailUrl, true, 'asset', asset.name)!);
                        return (
                          <div
                            key={`edit-asset-${index}-${asset.imageUrl || asset.name}`}
                            className={`character-select-card ${editRefImages.includes(fullImgUrl) ? 'selected' : ''}`}
                            onClick={() => {
                              setEditRefImages(prev => 
                                prev.includes(fullImgUrl) 
                                  ? prev.filter(url => url !== fullImgUrl)
                                  : [...prev, fullImgUrl]
                              );
                            }}
                          >
                            <img src={thumbnailUrl} alt={asset.name} />
                            <p className="character-name" title={asset.name}>
                              {asset.name.length > 15 ? asset.name.substring(0, 15) + '...' : asset.name}
                            </p>
                            {editRefImages.includes(fullImgUrl) && (
                              <div className="selected-badge">✓</div>
                            )}
                          </div>
                        );
                      })}
                      {editImageType === 'character' && storybook.characters.map((char, index) => {
                        if (!char.imageUrl) return null;
                        const fullImgUrl = getImageUrl(char.imageUrl);
                        const thumbnailUrl = getImageUrl(getThumbnailOrFullUrl(char.imageUrl, char.thumbnailUrl, true, 'character', char.name)!);
                        return (
                          <div
                            key={`edit-char-${index}-${char.imageUrl || char.name}`}
                            className={`character-select-card ${editRefImages.includes(fullImgUrl) ? 'selected' : ''}`}
                            onClick={() => {
                              setEditRefImages(prev => 
                                prev.includes(fullImgUrl) 
                                  ? prev.filter(url => url !== fullImgUrl)
                                  : [...prev, fullImgUrl]
                              );
                            }}
                          >
                            <img src={thumbnailUrl} alt={char.name} />
                            <p className="character-name" title={char.name}>
                              {char.name.length > 15 ? char.name.substring(0, 15) + '...' : char.name}
                            </p>
                            {editRefImages.includes(fullImgUrl) && (
                              <div className="selected-badge">✓</div>
                            )}
                          </div>
                        );
                      })}
                      {editImageType === 'background' && (storybook.backgrounds || []).map((bg, index) => {
                        if (!bg.imageUrl) return null;
                        const fullImgUrl = getImageUrl(bg.imageUrl);
                        const thumbnailUrl = getImageUrl(getThumbnailOrFullUrl(bg.imageUrl, bg.thumbnailUrl, true, 'background', bg.name)!);
                        return (
                          <div
                            key={`edit-bg-${index}-${bg.imageUrl || bg.name}`}
                            className={`character-select-card ${editRefImages.includes(fullImgUrl) ? 'selected' : ''}`}
                            onClick={() => {
                              setEditRefImages(prev => 
                                prev.includes(fullImgUrl) 
                                  ? prev.filter(url => url !== fullImgUrl)
                                  : [...prev, fullImgUrl]
                              );
                            }}
                          >
                            <img src={thumbnailUrl} alt={bg.name} />
                            <p className="character-name" title={bg.name}>
                              {bg.name.length > 15 ? bg.name.substring(0, 15) + '...' : bg.name}
                            </p>
                            {editRefImages.includes(fullImgUrl) && (
                              <div className="selected-badge">✓</div>
                            )}
                          </div>
                        );
                      })}
                      {editImageType === 'scene' && (
                        <>
                          {storybook.characters.map((char, index) => {
                            if (!char.imageUrl) return null;
                            const imgUrl = getImageUrl(char.imageUrl);
                            return (
                              <div
                                key={`char-${char.imageUrl || char.name || index}`}
                                className={`character-select-card ${editRefImages.includes(imgUrl) ? 'selected' : ''}`}
                                onClick={() => {
                                  setEditRefImages(prev => 
                                    prev.includes(imgUrl) 
                                      ? prev.filter(url => url !== imgUrl)
                                      : [...prev, imgUrl]
                                  );
                                }}
                              >
                                <img src={imgUrl} alt={char.name} />
                                <p className="character-name" title={char.name}>
                                  {char.name.length > 15 ? char.name.substring(0, 15) + '...' : char.name}
                                </p>
                                {editRefImages.includes(imgUrl) && (
                                  <div className="selected-badge">✓</div>
                                )}
                              </div>
                            );
                          })}
                          {(storybook.assets || []).map((asset, index) => {
                            if (!asset.imageUrl) return null;
                            const imgUrl = getImageUrl(asset.imageUrl);
                            return (
                              <div
                                key={`asset-${asset.imageUrl || asset.name || index}`}
                                className={`character-select-card ${editRefImages.includes(imgUrl) ? 'selected' : ''}`}
                                onClick={() => {
                                  setEditRefImages(prev => 
                                    prev.includes(imgUrl) 
                                      ? prev.filter(url => url !== imgUrl)
                                      : [...prev, imgUrl]
                                  );
                                }}
                              >
                                <img src={imgUrl} alt={asset.name} />
                                <p className="character-name" title={asset.name}>
                                  {asset.name.length > 15 ? asset.name.substring(0, 15) + '...' : asset.name}
                                </p>
                                {editRefImages.includes(imgUrl) && (
                                  <div className="selected-badge">✓</div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => {
                        if (animationFrameRef.current) {
                          cancelAnimationFrame(animationFrameRef.current);
                          animationFrameRef.current = null;
                        }
                        setShowEditModal(false);
                        setLassoPoints([]);
                        lassoPointsRef.current = [];
                        setLassoMode('none');
                      }}
                      disabled={isEditing}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isEditing || !editNewName.trim()}
                    >
                      {isEditing ? 'Editing...' : '✏️ Create Edited Image'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generation Progress Modal */}
      <GenerationProgressModal
        isOpen={showGenerationProgress}
        onClose={() => {
          setShowGenerationProgress(false);
          setGeneratedImageUrl(null);
        }}
        generationType={generationType}
        itemName={generationItemName}
        generatedImageUrl={generatedImageUrl}
        onRegenerate={lastGenerationParams ? handleRegenerate : undefined}
        model={currentGenerationModel}
      />

      {/* Gallery Modal */}
      <GalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        images={getGalleryImages()}
        onRename={handleGalleryRename}
        onDelete={handleGalleryDelete}
        onMoveToType={handleGalleryMoveToType}
      />

      {/* AI Transparency Loading Modal */}
      {showTransparencyLoadingModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: '500px' }}>
            <h3 style={{ marginBottom: '20px' }}>🎨 AI Transparency</h3>
            
            <div style={{ 
              padding: '40px 20px',
              fontSize: '18px',
              fontWeight: '500',
              color: '#333'
            }}>
              {transparencyLoadingMessage}
            </div>
            
            {!transparencyLoadingMessage.startsWith('❌') && !transparencyLoadingMessage.startsWith('✅') && (
              <div style={{
                margin: '20px auto',
                width: '60px',
                height: '60px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #7c3aed',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            
            {transparencyLoadingMessage.startsWith('✅') && (
              <div style={{ fontSize: '48px', margin: '20px 0' }}>✅</div>
            )}
            
            {transparencyLoadingMessage.startsWith('❌') && (
              <div style={{ fontSize: '48px', margin: '20px 0' }}>❌</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
